import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { useAuth } from "../../services/AuthContext";
import {
  getLanguageCopy,
  normalizeLanguage,
} from "../../services/language";
import { setLanguage as setI18nLanguage, t, t as i18nT } from "../../i18n";
import {
  BLOCK_TYPE_COMMON,
  BLOCK_TYPE_MATERIAL,
  BLOCK_TYPE_ANIMATION,
  BLOCK_TYPE_SPECIAL,
  COMMON_BLOCK_PRODUCTS,
  GIFT_CARD_PRODUCTS,
  MATERIAL_TEXTURE_PRODUCTS,
  BLOCK_ANIMATION_PRODUCTS,
  SPECIAL_BLOCK_PRODUCTS,
  STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER,
  STORE_PRODUCT_ACCESS_PURCHASE,
  TOP_WINNER_MODE_SKIN_BY_MODE,
  normalizeOwnedStoreProductIds,
  normalizeStoreProductId,
} from "../../constants/skinStoreCatalog";

const DEFAULT_STORE_SKIN_PRICE_X = 50;
const BOARD_GAME_PRODUCT_ID = "board_game_xima";
const BOARD_GAME_DEFAULT_PRICE_X = 100000;
const TOP_REWARD_PRODUCT_ID = "logo-xima-ciencia";
const GIFT_CARD_PRODUCT_IDS = Object.freeze(
  new Set(
    (GIFT_CARD_PRODUCTS || [])
      .map((product) => normalizeStoreProductId(product?.id))
      .filter(Boolean)
  )
);
const BOARD_GAME_GALLERY = Object.freeze([
  "/store/xima-board/xima-board-0.png",
  "/store/xima-board/xima-board-1.jpg",
  "/store/xima-board/xima-board-2.png",
  "/store/xima-board/xima-board-3.jpeg",
  "/store/xima-board/xima-board-4.jpeg",
  "/store/xima-board/xima-board-5.jpeg",
]);
const TOP_WINNER_MODE_KEYS = Object.freeze(Object.keys(TOP_WINNER_MODE_SKIN_BY_MODE));
const TOP_WINNER_MODE_LABELS = Object.freeze({
  ciencia: "CIENCIA",
  cienzar: "CIENZAR",
  sciencevsazar: "CONTRAZAR",
  azar: "AZAR",
});
const formatWalletBalanceX = (value) => `${Math.max(0, Math.round(Number(value) || 0))}X`;
const formatPriceNumberWithSpaces = (value) =>
  String(Math.max(0, Math.round(Number(value) || 0))).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
const formatPriceNumberForLanguage = (language, value) => {
  const normalizedValue = Math.max(0, Math.round(Number(value) || 0));
  if (language === "en") return normalizedValue.toLocaleString("en-US");
  if (language === "de") return normalizedValue.toLocaleString("de-DE");
  return String(normalizedValue).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};
const formatPriceXLabelForLanguage = (language, value) =>
  `${formatPriceNumberForLanguage(language, value)} X`;
const formatPriceXLabel = (value) => `${formatPriceNumberWithSpaces(value)} X`;
const getTopWinnerModeLabel = (modeKey) => {
  const normalized = String(modeKey || "").trim().toLowerCase();
  return TOP_WINNER_MODE_LABELS[normalized] || normalized.toUpperCase();
};
const formatGetForPriceLabel = (language, value) =>
  `${getStoreDirectCopy(language, "get", "OBTENER")} POR ${formatPriceNumberWithSpaces(value)}`;
const formatBuyForPriceLabel = (language, value) =>
  `${getStoreDirectCopy(language, "buyFor", "COMPRAR POR")} ${formatPriceNumberForLanguage(
    language,
    value
  )} X`;
const resolveLocaleForLanguage = (language) => {
  if (language === "en") return "en-US";
  if (language === "de") return "de-DE";
  if (language === "ru") return "ru-RU";
  return "es-ES";
};
const formatDateTimeForLanguage = (language, isoDate) => {
  const parsed = new Date(isoDate);
  if (!Number.isFinite(parsed.getTime())) return "--";
  try {
    return parsed.toLocaleString(resolveLocaleForLanguage(language), {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return parsed.toISOString();
  }
};
const padTwoDigits = (value) => String(Math.max(0, Number(value) || 0)).padStart(2, "0");
const formatCountdownClock = (valueMs) => {
  const totalSeconds = Math.max(0, Math.floor((Number(valueMs) || 0) / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${padTwoDigits(days)}:${padTwoDigits(hours)}:${padTwoDigits(minutes)}:${padTwoDigits(
    seconds
  )}`;
};
const getBonusesLabel = (language) => {
  if (language === "en") return "Bonuses";
  if (language === "de") return "Boni";
  if (language === "ru") return "Бонусы";
  return "BONOS";
};
const getAvailableWithBonusesLabel = (language) => {
  if (language === "en") return "AVAILABLE FOR PURCHASE VIA BONUSES";
  if (language === "de") return "VERFUEGBAR UEBER BONI";
  if (language === "ru") return "ДОСТУПНО ДЛЯ ПОКУПКИ ЗА БОНУСЫ";
  return "DISPONIBLE PARA COMPRA CON BONOS";
};

const parsePriceInput = (value) => {
  const sanitized = String(value || "").replace(/[^\d]/g, "");
  const parsed = Number(sanitized);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.round(parsed);
};

const parseNonNegativeIntegerInput = (value) => {
  const normalized = String(value ?? "").trim();
  if (!/^\d+$/.test(normalized)) return null;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed);
};

const isGiftCardProductId = (productId) =>
  GIFT_CARD_PRODUCT_IDS.has(normalizeStoreProductId(productId));

const createClientRequestId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return `order_${window.crypto.randomUUID().replace(/-/g, "")}`;
  }
  return `order_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};
const STORE_DIRECT_COPY = Object.freeze({
  intro: {
    en: "Customize your experience with AI-style designs for special and standard blocks.",
    ru: "Настройте свой опыт с помощью дизайнов в стиле ИИ для специальных и стандартных блоков.",
    de: "Gestalte dein Spielerlebnis individuell mit KI-gestützten Designs für Spezial- und Standardblöcke.",
  },
  wallet: { en: "WALLET", ru: "КОШЕЛЕК", de: "WALLET" },
  specialIntro: {
    en: "Each purchase unlocks the pack for use when editing special blocks.",
    ru: "Каждая покупка открывает набор для использования при редактировании специальных блоков.",
    de: "Mit jedem Kauf wird das Paket für die Bearbeitung von Spezialblöcken freigeschaltet.",
  },
  commonIntro: {
    en: "Each purchase unlocks the pack for use when editing standard blocks.",
    ru: "Каждая покупка открывает набор для использования при редактировании стандартных блоков.",
    de: "Mit jedem Kauf wird das Paket für die Bearbeitung von Standardblöcken freigeschaltet.",
  },
  enabled: { en: "ENABLED", ru: "ВКЛЮЧЕНО", de: "AKTIVIERT" },
  availableWallet: {
    en: "AVAILABLE FOR PURCHASE VIA WALLET",
    ru: "ДОСТУПНО ДЛЯ ПОКУПКИ ЧЕРЕЗ КОШЕЛЕК",
    de: "VERFÜGBAR ÜBER DIE WALLET",
  },
  earned: { en: "EARNED", ru: "ЗАРАБОТАНО", de: "VERDIENT" },
  get: { en: "GET", ru: "ПОЛУЧИТЬ", de: "ERHALTEN" },
  buyFor: { en: "Buy for", ru: "Купить за", de: "Kaufen für" },
  purchaseConfirmTemplate: {
    en: "This product requires {price}. Do you want to purchase it?",
    ru: "Этот продукт требует {price}. Хотите купить его?",
    de: "Dieses Produkt benötigt {price}. Möchtest du es kaufen?",
  },
  buy50: { en: "BUY 50 X", ru: "КУПИТЬ 50", de: "50 KAUFEN" },
  processing: { en: "PROCESSING...", ru: "ПРОВЕРКА...", de: "WIRD VERARBEITET..." },
  notAvailable: { en: "Not available", ru: "Недоступно", de: "Nicht verfügbar" },
  boardGameDesc: {
    en: "Official physical version to play at home, practice strategy, and share with friends.",
    ru: "Официальная физическая версия для игры дома, отработки стратегии и обмена опытом с друзьями.",
    de: "Offizielle physische Version zum Spielen zu Hause, zum Üben von Strategien und zum Teilen mit Freunden.",
  },
  plusDesc: {
    en: "Access to rewards, exclusive cosmetics, community perks, and an ad-free experience.",
    ru: "Доступ к наградам, эксклюзивной косметике, преимуществам сообщества и отсутствию рекламы.",
    de: "Zugang zu Prämien, exklusiven Kosmetikartikeln, Vorteilen der Community und einem werbefreien Erlebnis.",
  },
});

const getStoreDirectCopy = (language, key, es) => {
  if (language === "es") return es;
  return STORE_DIRECT_COPY[key]?.[language] || STORE_DIRECT_COPY[key]?.en || es;
};
const formatPurchaseConfirmationMessage = (language, priceX) =>
  getStoreDirectCopy(
    language,
    "purchaseConfirmTemplate",
    "Este producto requiere {price}. ¿Quieres comprarlo?"
  ).replace("{price}", formatPriceXLabelForLanguage(language, priceX));

const formatInsufficientBonusesMessage = (language, requiredX, availableX) => {
  const required = formatPriceXLabelForLanguage(language, requiredX);
  const available = formatPriceXLabelForLanguage(language, availableX);
  if (language === "en") {
    return `You do not have enough bonuses to obtain this card.\nYou need ${required} and you have ${available}.`;
  }
  if (language === "de") {
    return `Du hast nicht genug Boni, um diese Karte zu erhalten.\nDu benötigst ${required} und hast ${available}.`;
  }
  if (language === "ru") {
    return `У вас недостаточно бонусов, чтобы получить эту карту.\nВам нужно ${required}, а у вас есть ${available}.`;
  }
  return `No tienes bonos suficientes para obtener esta tarjeta.\nNecesitas ${required} y tienes ${available}.`;
};

const formatStorePrice = (language, price) =>
  String(price || "").trim().toUpperCase() === "GANADO"
    ? getStoreDirectCopy(language, "earned", "GANADO")
    : price;

const REWARD_CLAIM_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const REWARD_CLAIM_PHONE_PATTERN = /^\d{10,15}$/;
const REWARD_CLAIM_POSTAL_CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 -]{2,19}$/;
const REWARD_CLAIM_PLACEHOLDER_EMAIL_DOMAIN_PATTERN = /@(contact\.xima\.local|guest\.xima\.local)$/i;

const normalizeIdentity = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getIdentityLocalName = (value) => {
  const normalized = normalizeIdentity(value);
  if (!normalized || !normalized.includes("@")) return normalized;
  return normalizeIdentity(normalized.split("@")[0]);
};

const collectProfileIdentityCandidates = (profile = {}) => {
  const candidates = new Set();
  [
    profile?.loginIdentifier,
    profile?.email,
    profile?.username,
    profile?.userName,
    profile?.firstName,
    profile?.displayName,
  ].forEach((value) => {
    const normalized = normalizeIdentity(value);
    if (!normalized) return;
    candidates.add(normalized);
    const localName = getIdentityLocalName(normalized);
    if (localName) candidates.add(localName);
  });
  return candidates;
};

const resolveTopWinnerModesFromLeaderboard = (payload, identityCandidates) => {
  if (!identityCandidates || identityCandidates.size === 0) return [];
  const modes = payload?.modes && typeof payload.modes === "object" ? payload.modes : {};
  const result = [];

  TOP_WINNER_MODE_KEYS.forEach((modeKey) => {
    const players =
      Array.isArray(modes?.[modeKey])
        ? modes[modeKey]
        : Array.isArray(payload?.sections)
          ? payload.sections.find((section) => normalizeIdentity(section?.key) === modeKey)?.players
          : [];
    const topPlayer = Array.isArray(players) ? players[0] : null;
    const topIds = [
      topPlayer?.userId,
      topPlayer?.loginIdentifier,
      topPlayer?.email,
      topPlayer?.displayName,
    ]
      .flatMap((value) => [normalizeIdentity(value), getIdentityLocalName(value)])
      .filter(Boolean);

    if (topIds.some((value) => identityCandidates.has(value))) {
      result.push(modeKey);
    }
  });

  return result;
};

const Store = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromLobbySource = useMemo(
    () => String(new URLSearchParams(location.search).get("fromLobbySource") || "").toLowerCase(),
    [location.search]
  );
  const storeReturnRoute = fromLobbySource === "store" ? "/game/lobby" : "/game";
  const { user: authUser } = useAuth();
  const { language: outletLanguage = "es" } = useOutletContext() || {};
  const language = normalizeLanguage(outletLanguage);
  setI18nLanguage(language);
  const [selectedSpecialProduct, setSelectedSpecialProduct] = useState(null);
  const [selectedSpecialImage, setSelectedSpecialImage] = useState("");
  const [selectedCommonProduct, setSelectedCommonProduct] = useState(null);
  const [selectedCommonImage, setSelectedCommonImage] = useState("");
  const [isBoardProductModalOpen, setIsBoardProductModalOpen] = useState(false);
  const [selectedBoardImage, setSelectedBoardImage] = useState(BOARD_GAME_GALLERY[0]);
  const [isBoardCheckoutModalOpen, setIsBoardCheckoutModalOpen] = useState(false);
  const [boardCheckoutBusy, setBoardCheckoutBusy] = useState(false);
  const [boardOrderClientRequestId, setBoardOrderClientRequestId] = useState("");
  const [activeRewardProduct, setActiveRewardProduct] = useState(null);
  const [rewardClaimForm, setRewardClaimForm] = useState({
    firstName: "",
    lastName: "",
    contactEmail: "",
    phone: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    stateProvince: "",
    postalCode: "",
    country: "",
    confirmationAccepted: false,
  });
  const [ownedSpecialProductIds, setOwnedSpecialProductIds] = useState([]);
  const [ownedCommonProductIds, setOwnedCommonProductIds] = useState([]);
  const [ownedMaterialProductIds, setOwnedMaterialProductIds] = useState([]);
  const [ownedAnimationProductIds, setOwnedAnimationProductIds] = useState([]);
  const [isTopWinnerEligible, setIsTopWinnerEligible] = useState(false);
  const [topWinnerEligibleModes, setTopWinnerEligibleModes] = useState([]);
  const [scienceTopMonthlyRewardStatus, setScienceTopMonthlyRewardStatus] = useState(null);
  const [rewardCountdownNowMs, setRewardCountdownNowMs] = useState(() => Date.now());
  const [walletBalance, setWalletBalance] = useState(0);
  const [purchaseBusyKey, setPurchaseBusyKey] = useState("");
  const [highlightProductKey, setHighlightProductKey] = useState("");
  const [storePrices, setStorePrices] = useState({});
  const [editableProductIds, setEditableProductIds] = useState([]);
  const [isPriceEditMode, setIsPriceEditMode] = useState(false);
  const [priceUpdateBusyKey, setPriceUpdateBusyKey] = useState("");
  const [giftCardAvailabilityById, setGiftCardAvailabilityById] = useState({});
  const [giftCardAvailabilityMeta, setGiftCardAvailabilityMeta] = useState({
    dayKey: "",
    userClaimedToday: false,
    allSoldOut: false,
  });
  const [giftCardConfigDraftById, setGiftCardConfigDraftById] = useState({});
  const [giftCardConfigBusyKey, setGiftCardConfigBusyKey] = useState("");
  const [profileRole, setProfileRole] = useState("");
  const [isGuestSession, setIsGuestSession] = useState(false);
  const confirmResolverRef = useRef(null);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmRequiresChoice, setConfirmRequiresChoice] = useState(true);
  const roleCandidates = useMemo(
    () =>
      [
        authUser?.role,
        authUser?.type,
        authUser?.permission,
        authUser?.accountType,
        authUser?.accessLevel,
        profileRole,
      ]
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean),
    [
      authUser?.accessLevel,
      authUser?.accountType,
      authUser?.permission,
      authUser?.role,
      authUser?.type,
      profileRole,
    ]
  );
  const hasPriceEditAccess = useMemo(
    () => roleCandidates.some((value) => value === "admin" || value === "superuser"),
    [roleCandidates]
  );
  const openStoreConfirmation = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      confirmResolverRef.current = resolve;
      setConfirmRequiresChoice(options?.requiresChoice !== false);
      setConfirmMessage(String(message || "").trim());
      setIsConfirmModalOpen(true);
    });
  }, []);

  const openStoreNotice = useCallback(
    (message) => openStoreConfirmation(message, { requiresChoice: false }),
    [openStoreConfirmation]
  );

  const resolveStoreConfirmation = useCallback((accepted) => {
    const resolver = confirmResolverRef.current;
    confirmResolverRef.current = null;
    setIsConfirmModalOpen(false);
    setConfirmMessage("");
    setConfirmRequiresChoice(true);
    if (typeof resolver === "function") {
      resolver(Boolean(accepted));
    }
  }, []);

  useEffect(() => {
    return () => {
      if (typeof confirmResolverRef.current === "function") {
        confirmResolverRef.current(false);
      }
      confirmResolverRef.current = null;
    };
  }, []);

  const resolveProductPriceX = useCallback(
    (productId, fallbackPriceX) => {
      const normalizedProductId = normalizeStoreProductId(productId);
      if (!normalizedProductId) return Math.max(1, Math.round(Number(fallbackPriceX) || 0));
      const current = Number(storePrices?.[normalizedProductId]);
      if (Number.isFinite(current) && current > 0) return Math.round(current);
      return Math.max(1, Math.round(Number(fallbackPriceX) || 0));
    },
    [storePrices]
  );

  const boardGamePriceX = resolveProductPriceX(BOARD_GAME_PRODUCT_ID, BOARD_GAME_DEFAULT_PRICE_X);
  const giftCardAvailabilityList = useMemo(
    () =>
      (GIFT_CARD_PRODUCTS || [])
        .map((product) => {
          const productId = normalizeStoreProductId(product?.id);
          if (!productId) return null;
          const fallbackPriceX = Math.max(1, Math.round(Number(product?.defaultPriceX) || 0));
          const remoteCard =
            giftCardAvailabilityById && typeof giftCardAvailabilityById === "object"
              ? giftCardAvailabilityById[productId]
              : null;
          const priceX = Math.max(
            1,
            Math.round(
              Number(
                remoteCard?.priceX ?? resolveProductPriceX(productId, fallbackPriceX)
              ) || fallbackPriceX
            )
          );
          const maxDailyQuantity = Math.max(
            0,
            Math.round(Number(remoteCard?.maxDailyQuantity) || 0)
          );
          const availableToday = Math.max(
            0,
            Math.round(Number(remoteCard?.availableToday) || 0)
          );
          const isActive = remoteCard?.isActive === true;
          const isAvailableForClaim =
            remoteCard?.isAvailableForClaim === true ||
            (isActive && availableToday > 0 && maxDailyQuantity > 0);
          return {
            product,
            productId,
            priceX,
            maxDailyQuantity,
            availableToday,
            isActive,
            isAvailableForClaim,
            status: String(remoteCard?.status || "").trim().toLowerCase(),
          };
        })
        .filter(Boolean),
    [giftCardAvailabilityById, resolveProductPriceX]
  );
  const areAllGiftCardsSoldOut = useMemo(
    () =>
      giftCardAvailabilityList.length > 0
        ? giftCardAvailabilityList.every((entry) => !entry.isAvailableForClaim)
        : true,
    [giftCardAvailabilityList]
  );
  const selectedSpecialGallery = useMemo(() => {
    if (!selectedSpecialProduct) return [];
    const gallery = Array.isArray(selectedSpecialProduct.gallery)
      ? selectedSpecialProduct.gallery.filter(Boolean)
      : [];
    return gallery.length > 0 ? gallery : [selectedSpecialProduct.image].filter(Boolean);
  }, [selectedSpecialProduct]);
  const selectedCommonGallery = useMemo(() => {
    if (!selectedCommonProduct) return [];
    const gallery = Array.isArray(selectedCommonProduct.gallery)
      ? selectedCommonProduct.gallery.filter(Boolean)
      : [];
    return gallery.length > 0 ? gallery : [selectedCommonProduct.image].filter(Boolean);
  }, [selectedCommonProduct]);
  const ownedSpecialSet = useMemo(
    () => new Set(normalizeOwnedStoreProductIds(ownedSpecialProductIds, BLOCK_TYPE_SPECIAL)),
    [ownedSpecialProductIds]
  );
  const ownedCommonSet = useMemo(
    () => new Set(normalizeOwnedStoreProductIds(ownedCommonProductIds, BLOCK_TYPE_COMMON)),
    [ownedCommonProductIds]
  );
  const ownedMaterialSet = useMemo(
    () => new Set(normalizeOwnedStoreProductIds(ownedMaterialProductIds, BLOCK_TYPE_MATERIAL)),
    [ownedMaterialProductIds]
  );
  const ownedAnimationSet = useMemo(
    () => new Set(normalizeOwnedStoreProductIds(ownedAnimationProductIds, BLOCK_TYPE_ANIMATION)),
    [ownedAnimationProductIds]
  );
  const scienceTopMonthlyReward = useMemo(() => {
    const raw =
      scienceTopMonthlyRewardStatus &&
      typeof scienceTopMonthlyRewardStatus === "object" &&
      !Array.isArray(scienceTopMonthlyRewardStatus)
        ? scienceTopMonthlyRewardStatus
        : {};
    return {
      canClaim: Boolean(raw.canClaim),
      isEligible: Boolean(raw.isEligible),
      isScienceTopWinner: Boolean(raw.isScienceTopWinner),
      hasScienceTopSkinEquipped: Boolean(raw.hasScienceTopSkinEquipped),
      claimedByCurrentUser: Boolean(raw.claimedByCurrentUser),
      claimedByAnotherUser: Boolean(raw.claimedByAnotherUser),
      nextResetAt: String(raw.nextResetAt || "").trim(),
      msUntilNextReset: Math.max(0, Number(raw.msUntilNextReset) || 0),
    };
  }, [scienceTopMonthlyRewardStatus]);
  const scienceTopMonthlyRewardNextResetMs = useMemo(() => {
    const parsed = Date.parse(scienceTopMonthlyReward.nextResetAt);
    return Number.isFinite(parsed) ? parsed : NaN;
  }, [scienceTopMonthlyReward.nextResetAt]);
  const scienceTopMonthlyRewardTimeRemainingMs = useMemo(() => {
    if (Number.isFinite(scienceTopMonthlyRewardNextResetMs)) {
      return Math.max(0, scienceTopMonthlyRewardNextResetMs - rewardCountdownNowMs);
    }
    return Math.max(0, Number(scienceTopMonthlyReward.msUntilNextReset) || 0);
  }, [
    rewardCountdownNowMs,
    scienceTopMonthlyReward.msUntilNextReset,
    scienceTopMonthlyRewardNextResetMs,
  ]);
  const scienceTopMonthlyRewardNextResetLabel = useMemo(
    () => formatDateTimeForLanguage(language, scienceTopMonthlyReward.nextResetAt),
    [language, scienceTopMonthlyReward.nextResetAt]
  );
  const scienceTopMonthlyRewardTimeRemainingLabel = useMemo(
    () => formatCountdownClock(scienceTopMonthlyRewardTimeRemainingMs),
    [scienceTopMonthlyRewardTimeRemainingMs]
  );
  const scienceTopMonthlyRewardStatusLabel = (() => {
    if (scienceTopMonthlyReward.claimedByCurrentUser) {
      return t("SCIENCE_TOP_MONTHLY_STATUS_ALREADY_CLAIMED");
    }
    if (scienceTopMonthlyReward.claimedByAnotherUser) {
      return t("SCIENCE_TOP_MONTHLY_STATUS_ALREADY_CLAIMED_BY_ANOTHER");
    }
    if (scienceTopMonthlyReward.canClaim) {
      return t("SCIENCE_TOP_MONTHLY_STATUS_CAN_CLAIM");
    }
    if (!scienceTopMonthlyReward.isScienceTopWinner) {
      return t("SCIENCE_TOP_MONTHLY_STATUS_NOT_TOP");
    }
    if (!scienceTopMonthlyReward.hasScienceTopSkinEquipped) {
      return t("SCIENCE_TOP_MONTHLY_STATUS_SKIN_REQUIRED");
    }
    if (scienceTopMonthlyReward.isEligible) {
      return t("SCIENCE_TOP_MONTHLY_STATUS_ELIGIBLE_WAITING");
    }
    return t("SCIENCE_TOP_MONTHLY_STATUS_NOT_ELIGIBLE");
  })();

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setRewardCountdownNowMs(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const handleOpenSpecialProduct = useCallback((product) => {
    setSelectedSpecialProduct(product);
    const fallbackImage =
      Array.isArray(product?.gallery) && product.gallery[0] ? product.gallery[0] : product?.image || "";
    setSelectedSpecialImage(fallbackImage);
  }, []);

  const handleCloseSpecialProduct = useCallback(() => {
    setSelectedSpecialProduct(null);
    setSelectedSpecialImage("");
  }, []);
  const handleOpenCommonProduct = useCallback((product) => {
    setSelectedCommonProduct(product);
    const fallbackImage =
      Array.isArray(product?.gallery) && product.gallery[0] ? product.gallery[0] : product?.image || "";
    setSelectedCommonImage(fallbackImage);
  }, []);

  const handleCloseCommonProduct = useCallback(() => {
    setSelectedCommonProduct(null);
    setSelectedCommonImage("");
  }, []);

  const loadStorePrices = useCallback(async () => {
    try {
      const response = await axios.get("/auth/store/skins/prices");
      const nextPrices =
        response?.data?.prices &&
        typeof response.data.prices === "object" &&
        !Array.isArray(response.data.prices)
          ? response.data.prices
          : {};
      setStorePrices(nextPrices);
      setEditableProductIds(
        Array.isArray(response?.data?.editableProductIds)
          ? response.data.editableProductIds
              .map((value) => normalizeStoreProductId(value))
              .filter(Boolean)
          : []
      );
    } catch (error) {
      setStorePrices({
        [BOARD_GAME_PRODUCT_ID]: BOARD_GAME_DEFAULT_PRICE_X,
      });
      setEditableProductIds([]);
      console.log(error);
    }
  }, []);

  const loadGiftCardAvailability = useCallback(async () => {
    const buildDraftsFromCards = (cardsMap) => {
      const nextDrafts = {};
      (GIFT_CARD_PRODUCTS || []).forEach((product) => {
        const productId = normalizeStoreProductId(product?.id);
        if (!productId) return;
        const card = cardsMap?.[productId] || {};
        const fallbackPriceX = Math.max(1, Math.round(Number(product?.defaultPriceX) || 0));
        nextDrafts[productId] = {
          priceX: String(
            Math.max(1, Math.round(Number(card?.priceX) || fallbackPriceX))
          ),
          availableToday: String(
            Math.max(0, Math.round(Number(card?.availableToday) || 0))
          ),
          maxDailyQuantity: String(
            Math.max(0, Math.round(Number(card?.maxDailyQuantity) || 0))
          ),
          isActive: card?.isActive === true,
        };
      });
      return nextDrafts;
    };

    try {
      const response = await axios.get("/orders/gift-cards/availability");
      const cards = Array.isArray(response?.data?.cards) ? response.data.cards : [];
      const nextAvailabilityById = {};

      cards.forEach((card) => {
        const productId = normalizeStoreProductId(card?.productId);
        if (!productId) return;
        nextAvailabilityById[productId] = {
          productId,
          priceX: Math.max(1, Math.round(Number(card?.priceX) || 1)),
          availableToday: Math.max(0, Math.round(Number(card?.availableToday) || 0)),
          maxDailyQuantity: Math.max(0, Math.round(Number(card?.maxDailyQuantity) || 0)),
          isActive: card?.isActive === true,
          isAvailableForClaim: card?.isAvailableForClaim === true,
          status: String(card?.status || "").trim().toLowerCase(),
        };
      });

      (GIFT_CARD_PRODUCTS || []).forEach((product) => {
        const productId = normalizeStoreProductId(product?.id);
        if (!productId || nextAvailabilityById[productId]) return;
        nextAvailabilityById[productId] = {
          productId,
          priceX: Math.max(1, Math.round(Number(product?.defaultPriceX) || 1)),
          availableToday: 0,
          maxDailyQuantity: 0,
          isActive: false,
          isAvailableForClaim: false,
          status: "sold_out",
        };
      });

      setGiftCardAvailabilityById(nextAvailabilityById);
      setGiftCardAvailabilityMeta({
        dayKey: String(response?.data?.dayKey || "").trim(),
        userClaimedToday: Boolean(response?.data?.userClaimedToday),
        allSoldOut: Boolean(response?.data?.allSoldOut),
      });
      setGiftCardConfigDraftById(buildDraftsFromCards(nextAvailabilityById));

      setStorePrices((previous) => {
        const current =
          previous && typeof previous === "object" && !Array.isArray(previous)
            ? previous
            : {};
        const next = { ...current };
        let changed = false;
        Object.values(nextAvailabilityById).forEach((card) => {
          const productId = normalizeStoreProductId(card?.productId);
          if (!productId) return;
          const nextPriceX = Math.max(1, Math.round(Number(card?.priceX) || 1));
          if (Number(next[productId]) !== nextPriceX) {
            next[productId] = nextPriceX;
            changed = true;
          }
        });
        return changed ? next : current;
      });
    } catch (error) {
      const fallbackById = {};
      (GIFT_CARD_PRODUCTS || []).forEach((product) => {
        const productId = normalizeStoreProductId(product?.id);
        if (!productId) return;
        fallbackById[productId] = {
          productId,
          priceX: Math.max(1, Math.round(Number(product?.defaultPriceX) || 1)),
          availableToday: 0,
          maxDailyQuantity: 0,
          isActive: false,
          isAvailableForClaim: false,
          status: "sold_out",
        };
      });
      setGiftCardAvailabilityById(fallbackById);
      setGiftCardAvailabilityMeta({
        dayKey: "",
        userClaimedToday: false,
        allSoldOut: true,
      });
      setGiftCardConfigDraftById(buildDraftsFromCards(fallbackById));
      console.log(error);
    }
  }, []);

  const loadOwnedStoreProducts = useCallback(async () => {
    try {
      const response = await axios.get("/auth/profile");
      const profile = response?.data || {};
      const special = normalizeOwnedStoreProductIds(
        profile?.ownedSpecialSkinProductIds,
        BLOCK_TYPE_SPECIAL
      );
      const common = normalizeOwnedStoreProductIds(
        profile?.ownedCommonSkinProductIds,
        BLOCK_TYPE_COMMON
      );
      const materials = normalizeOwnedStoreProductIds(
        profile?.ownedMaterialTextureProductIds,
        BLOCK_TYPE_MATERIAL
      );
      const animations = normalizeOwnedStoreProductIds(
        profile?.ownedBlockAnimationProductIds,
        BLOCK_TYPE_ANIMATION
      );
      const profileTopModes = Array.isArray(profile?.skinEntitlements?.modes)
        ? profile.skinEntitlements.modes
        : [];
      let leaderboardTopModes = [];
      try {
        const leaderboardResponse = await axios.get("/tournaments/leaderboard/modes");
        leaderboardTopModes = resolveTopWinnerModesFromLeaderboard(
          leaderboardResponse?.data,
          collectProfileIdentityCandidates(profile)
        );
      } catch (leaderboardError) {
        console.log(leaderboardError);
      }
      const topModes = Array.from(
        new Set(
          [...profileTopModes, ...leaderboardTopModes]
            .map((modeKey) => String(modeKey || "").trim().toLowerCase())
            .filter((modeKey) => TOP_WINNER_MODE_KEYS.includes(modeKey))
        )
      );
      setOwnedSpecialProductIds(special);
      setOwnedCommonProductIds(common);
      setOwnedMaterialProductIds(materials);
      setOwnedAnimationProductIds(animations);
      setIsTopWinnerEligible(
        Boolean(profile?.skinEntitlements?.isTopWinner) || topModes.length > 0
      );
      setTopWinnerEligibleModes(topModes);
      setScienceTopMonthlyRewardStatus(
        profile?.scienceTopMonthlyReward &&
          typeof profile.scienceTopMonthlyReward === "object" &&
          !Array.isArray(profile.scienceTopMonthlyReward)
          ? profile.scienceTopMonthlyReward
          : null
      );
      setWalletBalance(profile?.walletBalance ?? profile?.wallet?.balance ?? 0);
      setProfileRole(String(profile?.role || "").trim().toLowerCase());
      setIsGuestSession(Boolean(profile?.isGuest));

      setRewardClaimForm((previous) => ({
        ...previous,
        firstName: previous.firstName || String(profile?.firstName || "").trim(),
        lastName: previous.lastName || String(profile?.lastName || "").trim(),
        contactEmail:
          previous.contactEmail ||
          String(profile?.contactEmail || profile?.email || "").trim().toLowerCase(),
        phone: previous.phone || String(profile?.phone || "").trim(),
        addressLine1:
          previous.addressLine1 || String(profile?.shippingAddressLine1 || "").trim(),
        addressLine2:
          previous.addressLine2 || String(profile?.shippingAddressLine2 || "").trim(),
        city: previous.city || String(profile?.shippingCity || "").trim(),
        stateProvince:
          previous.stateProvince || String(profile?.shippingStateProvince || "").trim(),
        postalCode:
          previous.postalCode || String(profile?.shippingPostalCode || "").trim(),
        country: previous.country || String(profile?.shippingCountry || "").trim(),
      }));
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    loadOwnedStoreProducts().catch((error) => console.log(error));
    loadStorePrices().catch((error) => console.log(error));
    loadGiftCardAvailability().catch((error) => console.log(error));
  }, [loadOwnedStoreProducts, loadStorePrices, loadGiftCardAvailability]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const focusedProductId = normalizeStoreProductId(params.get("product"));
    const focusedTypeRaw = String(params.get("type") || "").trim().toLowerCase();

    if (!focusedProductId) return;

    if (focusedTypeRaw === "board" && focusedProductId === BOARD_GAME_PRODUCT_ID) {
      setSelectedBoardImage(BOARD_GAME_GALLERY[0]);
      setIsBoardProductModalOpen(true);
      return;
    }

    const focusedType =
      focusedTypeRaw === BLOCK_TYPE_COMMON
        ? BLOCK_TYPE_COMMON
        : focusedTypeRaw === BLOCK_TYPE_MATERIAL
          ? BLOCK_TYPE_MATERIAL
          : focusedTypeRaw === BLOCK_TYPE_ANIMATION
            ? BLOCK_TYPE_ANIMATION
          : BLOCK_TYPE_SPECIAL;

    const focusKey = `${focusedType}:${focusedProductId}`;
    setHighlightProductKey(focusKey);
    const clearTimer = window.setTimeout(() => {
      setHighlightProductKey((current) => (current === focusKey ? "" : current));
    }, 3600);

    if (focusedType === BLOCK_TYPE_SPECIAL) {
      const focusedProduct = SPECIAL_BLOCK_PRODUCTS.find(
        (product) => normalizeStoreProductId(product?.id) === focusedProductId
      );
      if (focusedProduct) {
        handleOpenSpecialProduct(focusedProduct);
      }
    }

    if (focusedType === BLOCK_TYPE_COMMON) {
      const focusedProduct = COMMON_BLOCK_PRODUCTS.find(
        (product) => normalizeStoreProductId(product?.id) === focusedProductId
      );
      if (focusedProduct) {
        handleOpenCommonProduct(focusedProduct);
      }
    }

    window.requestAnimationFrame(() => {
      const target = document.getElementById(`store-${focusedType}-${focusedProductId}`);
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    });

    return () => {
      window.clearTimeout(clearTimer);
    };
  }, [handleOpenCommonProduct, handleOpenSpecialProduct, location.search]);

  const isOwnedProduct = useCallback(
    (blockType, productId, productAccessType = STORE_PRODUCT_ACCESS_PURCHASE) => {
      const normalizedId = normalizeStoreProductId(productId);
      if (!normalizedId) return false;
      if (productAccessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER) {
        const product =
          blockType === BLOCK_TYPE_COMMON
            ? COMMON_BLOCK_PRODUCTS.find((entry) => normalizeStoreProductId(entry?.id) === normalizedId)
            : SPECIAL_BLOCK_PRODUCTS.find((entry) => normalizeStoreProductId(entry?.id) === normalizedId);
        const rawRequiredMode = String(product?.earnedMode || "").trim().toLowerCase();
        const requiredMode = TOP_WINNER_MODE_KEYS.includes(rawRequiredMode)
          ? rawRequiredMode
          : "";
        if (rawRequiredMode && !requiredMode) return false;
        if (requiredMode) {
          return topWinnerEligibleModes
            .map((modeKey) => String(modeKey || "").trim().toLowerCase())
            .includes(requiredMode);
        }
        return isTopWinnerEligible;
      }
      if (blockType === BLOCK_TYPE_COMMON) return ownedCommonSet.has(normalizedId);
      if (blockType === BLOCK_TYPE_MATERIAL) return ownedMaterialSet.has(normalizedId);
      if (blockType === BLOCK_TYPE_ANIMATION) return ownedAnimationSet.has(normalizedId);
      return ownedSpecialSet.has(normalizedId);
    },
    [isTopWinnerEligible, ownedAnimationSet, ownedCommonSet, ownedMaterialSet, ownedSpecialSet, topWinnerEligibleModes]
  );

  const handleBuyProduct = useCallback(
    async (product, blockType) => {
      const normalizedProductId = normalizeStoreProductId(product?.id);
      if (!normalizedProductId) return;
      const accessType =
        product?.accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
          ? STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
          : STORE_PRODUCT_ACCESS_PURCHASE;
      const isGiftCard = isGiftCardProductId(normalizedProductId);

      if (accessType === STORE_PRODUCT_ACCESS_PURCHASE) {
        if (isGiftCard) {
          if (giftCardAvailabilityMeta?.userClaimedToday) {
            window.alert(t("giftCardDailyUserLimitMessage"));
            return;
          }

          const giftCardAvailability =
            giftCardAvailabilityById && typeof giftCardAvailabilityById === "object"
              ? giftCardAvailabilityById[normalizedProductId]
              : null;
          if (!giftCardAvailability?.isAvailableForClaim) {
            window.alert(t("giftCardSoldOutMessage"));
            return;
          }

          const productPriceX = Math.max(
            1,
            Math.round(
              Number(
                giftCardAvailability?.priceX ??
                  resolveProductPriceX(normalizedProductId, DEFAULT_STORE_SKIN_PRICE_X)
              ) || DEFAULT_STORE_SKIN_PRICE_X
            )
          );
          if (walletBalance < productPriceX) {
            await openStoreNotice(
              formatInsufficientBonusesMessage(language, productPriceX, walletBalance)
            );
            return;
          }
          setActiveRewardProduct({
            id: normalizedProductId,
            name: getLanguageCopy(language, product?.name),
            priceX: productPriceX,
          });
          setBoardOrderClientRequestId(createClientRequestId());
          setRewardClaimForm((previous) => ({
            ...previous,
            confirmationAccepted: false,
          }));
          setIsBoardCheckoutModalOpen(true);
          return;
        }

        if (isOwnedProduct(blockType, normalizedProductId, accessType)) {
          window.alert(i18nT("store.esteProductoYaEstaDesbloqueadoEnTuCuenta"));
          return;
        }
        const productPriceX = resolveProductPriceX(normalizedProductId, DEFAULT_STORE_SKIN_PRICE_X);
        const acceptedPurchase = await openStoreConfirmation(
          formatPurchaseConfirmationMessage(language, productPriceX)
        );
        if (!acceptedPurchase) return;

        const busyKey = `${blockType}:${normalizedProductId}`;
        try {
          setPurchaseBusyKey(busyKey);
          const response = await axios.post("/auth/store/skins/purchase", {
            productId: normalizedProductId,
            blockType,
          });

          const nextSpecial = normalizeOwnedStoreProductIds(
            response?.data?.ownedSpecialSkinProductIds,
            BLOCK_TYPE_SPECIAL
          );
          const nextCommon = normalizeOwnedStoreProductIds(
            response?.data?.ownedCommonSkinProductIds,
            BLOCK_TYPE_COMMON
          );
          const nextMaterials = normalizeOwnedStoreProductIds(
            response?.data?.ownedMaterialTextureProductIds,
            BLOCK_TYPE_MATERIAL
          );
          const nextAnimations = normalizeOwnedStoreProductIds(
            response?.data?.ownedBlockAnimationProductIds,
            BLOCK_TYPE_ANIMATION
          );

          setOwnedSpecialProductIds(
            nextSpecial.length > 0
              ? nextSpecial
              : Array.from(
                  new Set(
                    blockType === BLOCK_TYPE_SPECIAL
                      ? [...ownedSpecialSet, normalizedProductId]
                      : [...ownedSpecialSet]
                  )
                )
          );
          setOwnedCommonProductIds(
            nextCommon.length > 0
              ? nextCommon
              : Array.from(
                  new Set(
                    blockType === BLOCK_TYPE_COMMON
                      ? [...ownedCommonSet, normalizedProductId]
                      : [...ownedCommonSet]
                  )
                )
          );
          setOwnedMaterialProductIds(
            nextMaterials.length > 0
              ? nextMaterials
              : Array.from(
                  new Set(
                    blockType === BLOCK_TYPE_MATERIAL
                      ? [...ownedMaterialSet, normalizedProductId]
                      : [...ownedMaterialSet]
                  )
                )
          );
          setOwnedAnimationProductIds(
            nextAnimations.length > 0
              ? nextAnimations
              : Array.from(
                  new Set(
                    blockType === BLOCK_TYPE_ANIMATION
                      ? [...ownedAnimationSet, normalizedProductId]
                      : [...ownedAnimationSet]
                  )
                )
          );
          const nextWalletBalance =
            response?.data?.walletBalance ??
            response?.data?.wallet?.balance ??
            response?.data?.user?.walletBalance ??
            response?.data?.user?.wallet?.balance;
          if (nextWalletBalance !== undefined && nextWalletBalance !== null) {
            setWalletBalance(nextWalletBalance);
          }

          window.alert(response?.data?.message || "Compra completada con exito.");
        } catch (error) {
          const nextWalletBalance =
            error?.response?.data?.walletBalance ??
            error?.response?.data?.wallet?.balance ??
            error?.response?.data?.user?.walletBalance ??
            error?.response?.data?.user?.wallet?.balance;
          if (nextWalletBalance !== undefined && nextWalletBalance !== null) {
            setWalletBalance(nextWalletBalance);
          }
          window.alert(
            error?.response?.data?.message ||
              error?.response?.data?.error ||
              "No se pudo completar este proceso."
          );
        } finally {
          setPurchaseBusyKey("");
        }
        return;
      }

      if (normalizedProductId !== TOP_REWARD_PRODUCT_ID) {
        const modeKey = getTopWinnerModeLabel(product?.earnedMode);
        window.alert(
          modeKey
            ? `Este logo se desbloquea solo para el mejor jugador del modo ${modeKey}.`
            : "Este logo se desbloquea solo para el mejor jugador del modo correspondiente."
        );
        return;
      }

      const busyKey = `${blockType}:${normalizedProductId}`;
      try {
        setPurchaseBusyKey(busyKey);
        const response = await axios.post("/auth/store/skins/claim-top-reward", {
          productId: normalizedProductId,
          blockType,
        });
        const nextRewardStatus =
          response?.data?.scienceTopMonthlyReward &&
          typeof response.data.scienceTopMonthlyReward === "object" &&
          !Array.isArray(response.data.scienceTopMonthlyReward)
            ? response.data.scienceTopMonthlyReward
            : null;
        if (nextRewardStatus) {
          setScienceTopMonthlyRewardStatus(nextRewardStatus);
        }
        const nextWalletBalance =
          response?.data?.walletBalance ??
          response?.data?.wallet?.balance ??
          response?.data?.user?.walletBalance ??
          response?.data?.user?.wallet?.balance;
        if (nextWalletBalance !== undefined && nextWalletBalance !== null) {
          setWalletBalance(nextWalletBalance);
        }
        await loadOwnedStoreProducts();
        window.alert(
          response?.data?.message ||
            "Reclamo enviado. Si eres el jugador #1, se aplicara tu beneficio del mes."
        );
      } catch (error) {
        const nextRewardStatus =
          error?.response?.data?.scienceTopMonthlyReward &&
          typeof error.response.data.scienceTopMonthlyReward === "object" &&
          !Array.isArray(error.response.data.scienceTopMonthlyReward)
            ? error.response.data.scienceTopMonthlyReward
            : null;
        if (nextRewardStatus) {
          setScienceTopMonthlyRewardStatus(nextRewardStatus);
        }
        window.alert(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            "No se pudo completar este proceso."
        );
      } finally {
        setPurchaseBusyKey("");
      }
    },
    [
      isOwnedProduct,
      loadOwnedStoreProducts,
      ownedCommonSet,
      ownedAnimationSet,
      ownedMaterialSet,
      ownedSpecialSet,
      language,
      openStoreConfirmation,
      openStoreNotice,
      giftCardAvailabilityById,
      giftCardAvailabilityMeta?.userClaimedToday,
      resolveProductPriceX,
      walletBalance,
    ]
  );

  const canEditPriceForProduct = useCallback(
    (productId, accessType = STORE_PRODUCT_ACCESS_PURCHASE) => {
      if (!hasPriceEditAccess) return false;
      const normalizedProductId = normalizeStoreProductId(productId);
      if (!normalizedProductId) return false;
      if (accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER) return false;
      if (editableProductIds.length === 0) {
        return (
          accessType === STORE_PRODUCT_ACCESS_PURCHASE || normalizedProductId === BOARD_GAME_PRODUCT_ID
        );
      }
      return editableProductIds.includes(normalizedProductId);
    },
    [editableProductIds, hasPriceEditAccess]
  );

  const handleEditProductPrice = useCallback(
    async (productId, currentPriceX, productNameLabel = "") => {
      if (!hasPriceEditAccess || !isPriceEditMode) return;
      const normalizedProductId = normalizeStoreProductId(productId);
      if (!normalizedProductId) return;

      const nextRaw = window.prompt(
        `Nuevo precio para ${productNameLabel || normalizedProductId} (solo numero):`,
        String(Math.max(1, Math.round(Number(currentPriceX) || 0)))
      );
      if (nextRaw === null) return;

      const nextPriceX = parsePriceInput(nextRaw);
      if (!nextPriceX) {
        window.alert(i18nT("store.debesEscribirUnPrecioValidoMayorQueCero"));
        return;
      }

      const busyKey = `price:${normalizedProductId}`;
      try {
        setPriceUpdateBusyKey(busyKey);
        const response = await axios.patch(`/auth/store/skins/prices/${normalizedProductId}`, {
          priceX: nextPriceX,
        });
        const nextPrices = response?.data?.prices;
        if (nextPrices && typeof nextPrices === "object" && !Array.isArray(nextPrices)) {
          setStorePrices(nextPrices);
        } else {
          await loadStorePrices();
        }
        if (Array.isArray(response?.data?.editableProductIds)) {
          setEditableProductIds(
            response.data.editableProductIds
              .map((value) => normalizeStoreProductId(value))
              .filter(Boolean)
          );
        }
        if (isGiftCardProductId(normalizedProductId)) {
          await loadGiftCardAvailability();
        }
        window.alert(response?.data?.message || "Precio actualizado correctamente.");
      } catch (error) {
        const message =
          error?.response?.data?.message || "No se pudo actualizar el precio de los productos.";
        const reason = String(error?.response?.data?.reason || "").trim();
        window.alert(reason ? `${message}\nMotivo: ${reason}` : message);
      } finally {
        setPriceUpdateBusyKey("");
      }
    },
    [hasPriceEditAccess, isPriceEditMode, loadStorePrices, loadGiftCardAvailability]
  );

  const handleGiftCardConfigFieldChange = useCallback((productId, field, value) => {
    const normalizedProductId = normalizeStoreProductId(productId);
    if (!normalizedProductId) return;
    setGiftCardConfigDraftById((previous) => {
      const current = previous && typeof previous === "object" ? previous : {};
      const currentEntry = current[normalizedProductId] || {
        priceX: "",
        availableToday: "",
        maxDailyQuantity: "",
        isActive: false,
      };
      return {
        ...current,
        [normalizedProductId]: {
          ...currentEntry,
          [field]:
            field === "isActive"
              ? Boolean(value)
              : String(value ?? "").replace(/[^\d]/g, ""),
        },
      };
    });
  }, []);

  const handleSaveGiftCardDailyConfig = useCallback(
    async (productId) => {
      if (!hasPriceEditAccess || !isPriceEditMode) return;
      const normalizedProductId = normalizeStoreProductId(productId);
      if (!normalizedProductId) return;

      const draft =
        giftCardConfigDraftById && typeof giftCardConfigDraftById === "object"
          ? giftCardConfigDraftById[normalizedProductId]
          : null;
      if (!draft) {
        window.alert(t("giftCardAdminInvalidConfig"));
        return;
      }

      const priceX = parsePriceInput(draft.priceX);
      if (!priceX) {
        window.alert(t("giftCardAdminInvalidPrice"));
        return;
      }

      const maxDailyQuantity = parseNonNegativeIntegerInput(draft.maxDailyQuantity);
      if (maxDailyQuantity === null) {
        window.alert(t("giftCardAdminInvalidMaxDaily"));
        return;
      }

      const availableToday = parseNonNegativeIntegerInput(draft.availableToday);
      if (availableToday === null) {
        window.alert(t("giftCardAdminInvalidAvailableToday"));
        return;
      }

      const busyKey = `gift-config:${normalizedProductId}`;
      try {
        setGiftCardConfigBusyKey(busyKey);
        const response = await axios.patch(
          `/orders/gift-cards/admin/config/${normalizedProductId}`,
          {
            priceX,
            maxDailyQuantity,
            availableToday: Math.min(availableToday, maxDailyQuantity),
            isActive: draft.isActive === true,
          }
        );

        const snapshotCards = Array.isArray(response?.data?.snapshot?.cards)
          ? response.data.snapshot.cards
          : [];
        const singleCard =
          response?.data?.card &&
          typeof response.data.card === "object" &&
          !Array.isArray(response.data.card)
            ? response.data.card
            : null;
        const updatedCards = snapshotCards.length > 0 ? snapshotCards : singleCard ? [singleCard] : [];

        if (updatedCards.length > 0) {
          setGiftCardAvailabilityById((previous) => {
            const current =
              previous && typeof previous === "object" && !Array.isArray(previous)
                ? previous
                : {};
            const next = { ...current };
            updatedCards.forEach((card) => {
              const cardProductId = normalizeStoreProductId(card?.productId);
              if (!cardProductId) return;
              next[cardProductId] = {
                productId: cardProductId,
                priceX: Math.max(1, Math.round(Number(card?.priceX) || 1)),
                availableToday: Math.max(0, Math.round(Number(card?.availableToday) || 0)),
                maxDailyQuantity: Math.max(0, Math.round(Number(card?.maxDailyQuantity) || 0)),
                isActive: card?.isActive === true,
                isAvailableForClaim: card?.isAvailableForClaim === true,
                status: String(card?.status || "").trim().toLowerCase(),
              };
            });
            return next;
          });

          setStorePrices((previous) => {
            const current =
              previous && typeof previous === "object" && !Array.isArray(previous)
                ? previous
                : {};
            const next = { ...current };
            let changed = false;
            updatedCards.forEach((card) => {
              const cardProductId = normalizeStoreProductId(card?.productId);
              if (!cardProductId) return;
              const nextPriceX = Math.max(1, Math.round(Number(card?.priceX) || 1));
              if (Number(next[cardProductId]) !== nextPriceX) {
                next[cardProductId] = nextPriceX;
                changed = true;
              }
            });
            return changed ? next : current;
          });
        }

        if (response?.data?.snapshot) {
          setGiftCardAvailabilityMeta({
            dayKey: String(response?.data?.snapshot?.dayKey || "").trim(),
            userClaimedToday: Boolean(response?.data?.snapshot?.userClaimedToday),
            allSoldOut: Boolean(response?.data?.snapshot?.allSoldOut),
          });
        } else {
          await loadGiftCardAvailability();
        }

        window.alert(response?.data?.message || t("giftCardAdminConfigSaved"));
      } catch (error) {
        const message =
          error?.response?.data?.message || t("giftCardAdminConfigSaveError");
        const reason = String(error?.response?.data?.reason || "").trim();
        window.alert(reason ? `${message}\n${t("reason")}: ${reason}` : message);
      } finally {
        setGiftCardConfigBusyKey("");
      }
    },
    [
      hasPriceEditAccess,
      isPriceEditMode,
      giftCardConfigDraftById,
      loadGiftCardAvailability,
    ]
  );

  const handleOpenBoardProduct = useCallback(() => {
    setSelectedBoardImage(BOARD_GAME_GALLERY[0]);
    setIsBoardProductModalOpen(true);
  }, []);

  const handleCloseBoardProduct = useCallback(() => {
    setIsBoardProductModalOpen(false);
  }, []);

  const handleOpenBoardCheckout = useCallback(() => {
    if (isGuestSession) {
      window.alert(t("guestRewardClaimRequired"));
      navigate("/register");
      return;
    }
    setActiveRewardProduct({
      id: BOARD_GAME_PRODUCT_ID,
      name: t("JUEGO_DE_MESA_XIMA"),
      priceX: boardGamePriceX,
    });
    setBoardOrderClientRequestId(createClientRequestId());
    setRewardClaimForm((previous) => ({
      ...previous,
      confirmationAccepted: false,
    }));
    setIsBoardCheckoutModalOpen(true);
  }, [boardGamePriceX, isGuestSession, navigate]);

  const handleCloseBoardCheckout = useCallback(() => {
    if (boardCheckoutBusy) return;
    setIsBoardCheckoutModalOpen(false);
    setActiveRewardProduct(null);
  }, [boardCheckoutBusy]);

  const handleRewardClaimFieldChange = useCallback((field, value) => {
    setRewardClaimForm((previous) => ({
      ...previous,
      [field]:
        field === "confirmationAccepted"
          ? Boolean(value)
          : String(value || "").slice(0, field === "phone" ? 40 : 240),
    }));
  }, []);

  const handleConfirmBoardPurchase = useCallback(async () => {
    if (isGuestSession) {
      window.alert(t("guestRewardClaimRequired"));
      navigate("/register");
      return;
    }

    const requiredField = [
      "firstName",
      "lastName",
      "contactEmail",
      "phone",
      "country",
      "stateProvince",
      "city",
      "addressLine1",
      "postalCode",
    ].find((field) => !String(rewardClaimForm?.[field] || "").trim());

    if (requiredField) {
      const requiredMessagesByField = {
        firstName: t("requiredFirstName"),
        lastName: t("requiredLastName"),
        contactEmail: t("requiredContactEmail"),
        phone: t("requiredPhone"),
        country: t("requiredCountry"),
        stateProvince: t("requiredState"),
        city: t("requiredCity"),
        addressLine1: t("requiredAddressLine1"),
        postalCode: t("requiredPostalCode"),
      };
      window.alert(requiredMessagesByField[requiredField] || t("requiredCity"));
      return;
    }

    const normalizedEmail = String(rewardClaimForm.contactEmail || "")
      .trim()
      .toLowerCase();
    if (!REWARD_CLAIM_EMAIL_PATTERN.test(normalizedEmail)) {
      window.alert(t("invalidContactEmail"));
      return;
    }
    if (REWARD_CLAIM_PLACEHOLDER_EMAIL_DOMAIN_PATTERN.test(normalizedEmail)) {
      window.alert(t("invalidContactEmailPlaceholder"));
      return;
    }

    const normalizedPhone = String(rewardClaimForm.phone || "").replace(/[^\d]/g, "");
    if (!REWARD_CLAIM_PHONE_PATTERN.test(normalizedPhone)) {
      window.alert(t("invalidPhone"));
      return;
    }

    const normalizedPostalCode = String(rewardClaimForm.postalCode || "").trim();
    if (!REWARD_CLAIM_POSTAL_CODE_PATTERN.test(normalizedPostalCode)) {
      window.alert(t("invalidPostalCode"));
      return;
    }

    if (!rewardClaimForm.confirmationAccepted) {
      window.alert(t("confirmRealDataRequired"));
      return;
    }

    const selectedProductId = normalizeStoreProductId(activeRewardProduct?.id) || BOARD_GAME_PRODUCT_ID;
    const isGiftCardClaim = isGiftCardProductId(selectedProductId);
    if (isGiftCardClaim && giftCardAvailabilityMeta?.userClaimedToday) {
      window.alert(t("giftCardDailyUserLimitMessage"));
      return;
    }
    if (isGiftCardClaim && !giftCardAvailabilityById?.[selectedProductId]?.isAvailableForClaim) {
      window.alert(t("giftCardSoldOutMessage"));
      return;
    }
    const selectedProductPriceX = Math.max(
      1,
      Math.round(Number(activeRewardProduct?.priceX || boardGamePriceX) || 0)
    );
    if (walletBalance < selectedProductPriceX) {
      await openStoreNotice(
        formatInsufficientBonusesMessage(language, selectedProductPriceX, walletBalance)
      );
      return;
    }

    const confirmPurchasePrompt = String(t("confirmPurchaseMessage")).replace(
      /[\d.,\s]+X/,
      formatPriceXLabel(selectedProductPriceX)
    );
    const accepted = await openStoreConfirmation(
      `${confirmPurchasePrompt}\n${t("rewardClaimInfoWarning")}`
    );
    if (!accepted) return;

    const clientRequestId =
      String(boardOrderClientRequestId || "").trim() || createClientRequestId();

    try {
      setBoardCheckoutBusy(true);
      const response = await axios.post(isGiftCardClaim ? "/orders/gift-cards/claim" : "/orders/create", {
        productId: selectedProductId,
        quantity: 1,
        clientRequestId,
        confirmationAccepted: true,
        contactEmail: normalizedEmail,
        shippingAddressLine1: String(rewardClaimForm.addressLine1 || "").trim(),
        shippingAddressLine2: String(rewardClaimForm.addressLine2 || "").trim(),
        shippingCity: String(rewardClaimForm.city || "").trim(),
        shippingStateProvince: String(rewardClaimForm.stateProvince || "").trim(),
        shippingPostalCode: normalizedPostalCode,
        shippingCountry: String(rewardClaimForm.country || "").trim(),
        rewardProfile: {
          firstName: String(rewardClaimForm.firstName || "").trim(),
          lastName: String(rewardClaimForm.lastName || "").trim(),
          contactEmail: normalizedEmail,
          phone: normalizedPhone,
          country: String(rewardClaimForm.country || "").trim(),
          stateProvince: String(rewardClaimForm.stateProvince || "").trim(),
          city: String(rewardClaimForm.city || "").trim(),
          addressLine1: String(rewardClaimForm.addressLine1 || "").trim(),
          addressLine2: String(rewardClaimForm.addressLine2 || "").trim(),
          postalCode: normalizedPostalCode,
          confirmationAccepted: true,
        },
        shippingAddress: {
          fullName: `${String(rewardClaimForm.firstName || "").trim()} ${String(
            rewardClaimForm.lastName || ""
          ).trim()}`.trim(),
          line1: String(rewardClaimForm.addressLine1 || "").trim(),
          line2: String(rewardClaimForm.addressLine2 || "").trim(),
          city: String(rewardClaimForm.city || "").trim(),
          stateProvince: String(rewardClaimForm.stateProvince || "").trim(),
          postalCode: normalizedPostalCode,
          country: String(rewardClaimForm.country || "").trim(),
        },
        phone: normalizedPhone,
      });

      const nextWalletBalance =
        response?.data?.walletBalance ??
        response?.data?.wallet?.balance ??
        response?.data?.user?.walletBalance ??
        response?.data?.user?.wallet?.balance;
      if (nextWalletBalance !== undefined && nextWalletBalance !== null) {
        setWalletBalance(nextWalletBalance);
      }

      await loadOwnedStoreProducts();
      await loadGiftCardAvailability();
      window.alert(t("rewardClaimSuccessMessage"));
      setIsBoardCheckoutModalOpen(false);
      setIsBoardProductModalOpen(false);
      setActiveRewardProduct(null);
      setBoardOrderClientRequestId(createClientRequestId());
    } catch (error) {
      const message = error?.response?.data?.message || t("shippingOrderError");
      const reason = String(
        error?.response?.data?.reason || error?.response?.data?.error || ""
      ).trim();
      window.alert(reason ? `${message}\n${t("reason")}: ${reason}` : message);
    } finally {
      setBoardCheckoutBusy(false);
    }
  }, [
    boardGamePriceX,
    boardOrderClientRequestId,
    isGuestSession,
    navigate,
    openStoreConfirmation,
    openStoreNotice,
    rewardClaimForm,
    activeRewardProduct,
    walletBalance,
    language,
    giftCardAvailabilityById,
    giftCardAvailabilityMeta?.userClaimedToday,
    loadGiftCardAvailability,
    loadOwnedStoreProducts,
  ]);

  return (
    <div className="h-full overflow-auto px-4 pb-28 pt-4 text-white md:px-6 md:pb-32 md:pt-6">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
        <section className="rounded-2xl border border-[#2c4e88] bg-[linear-gradient(135deg,rgba(8,23,52,0.95),rgba(21,38,97,0.92),rgba(29,21,86,0.94))] p-5 shadow-[0_0_36px_rgba(0,145,255,0.16)]">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.24em] text-[#8ac6ff]">
                {t("TIENDA_OFICIAL")}
              </div>
              <h1 className="mt-1 text-3xl font-black uppercase tracking-wide text-white md:text-4xl">
                {t("TIENDA_XIMA")}
              </h1>
              <p className="mt-2 max-w-3xl text-sm text-[#c4d9ff] md:text-base">
                {getStoreDirectCopy(
                  language,
                  "intro",
                  "Personaliza tu experiencia con disenos estilo IA para bloques especiales y bloques comunes."
                )}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="rounded-lg border border-[#f5d684] bg-[#4a370f] px-4 py-2 text-sm font-black uppercase tracking-wide text-[#ffe7aa]">
                {getBonusesLabel(language)}: {formatWalletBalanceX(walletBalance)}
              </div>

              {hasPriceEditAccess ? (
                <button
                  type="button"
                  onClick={() => setIsPriceEditMode((current) => !current)}
                  className={`rounded border px-4 py-2 text-xs font-bold uppercase tracking-wide transition ${
                    isPriceEditMode
                      ? "border-[#ffdf8a] bg-[#5a3d08] text-[#ffe8b4]"
                      : "border-[#8dc7ff] bg-[#244b78] text-[#e1f2ff] hover:border-[#b1dcff]"
                  }`}
                >
                  {isPriceEditMode ? t("storeFinishEditPrices") : t("storeEditPricesButton")}
                </button>
              ) : null}

              <button
                type="button"
                onClick={() => navigate(storeReturnRoute)}
                className="rounded border border-[#63d5ff] bg-[#10315b] px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#d9f3ff] transition hover:border-[#89e5ff]"
              >
                {t("VOLVER_AL_LOBBY")}
              </button>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-[#2f4d7a] bg-[#081631d8] p-4 shadow-[0_0_26px_rgba(5,11,32,0.45)]">
          <div className="mb-4 flex flex-col gap-1">
            <h2 className="text-xl font-black uppercase tracking-wide text-[#9ed6ff]">
              {t("SKINS_PARA_BLOQUES_ESPECIALES")}
            </h2>
            <p className="text-sm text-[#b6cbed]">
              {getStoreDirectCopy(
                language,
                "specialIntro",
                "Cada compra desbloquea el paquete para usarlo en la edicion de bloques especiales."
              )}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {SPECIAL_BLOCK_PRODUCTS.map((product) => {
              const normalizedProductId = normalizeStoreProductId(product?.id);
              const cardKey = `${BLOCK_TYPE_SPECIAL}:${normalizedProductId}`;
              const accessType =
                product?.accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
                  ? STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
                  : STORE_PRODUCT_ACCESS_PURCHASE;
              const earnedMode = String(product?.earnedMode || "").trim().toLowerCase();
              const owned = isOwnedProduct(
                BLOCK_TYPE_SPECIAL,
                normalizedProductId,
                accessType
              );
              const isBusy = purchaseBusyKey === cardKey;
              const isFocused = highlightProductKey === cardKey;
              const productPriceX = resolveProductPriceX(
                normalizedProductId,
                DEFAULT_STORE_SKIN_PRICE_X
              );
              const canEditPrice = canEditPriceForProduct(normalizedProductId, accessType);
              const isUpdatingPrice = priceUpdateBusyKey === `price:${normalizedProductId}`;
              const isScienceTopRewardProduct =
                normalizedProductId === TOP_REWARD_PRODUCT_ID &&
                accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER;
              const scienceTopStatusToneClass = scienceTopMonthlyReward.canClaim
                ? "text-[#8bffb5]"
                : scienceTopMonthlyReward.claimedByCurrentUser
                  ? "text-[#ffe69a]"
                  : scienceTopMonthlyReward.claimedByAnotherUser
                    ? "text-[#ffb7b7]"
                    : "text-[#d2e3ff]";

              return (
                <article
                  key={normalizedProductId}
                  id={`store-${BLOCK_TYPE_SPECIAL}-${normalizedProductId}`}
                  className={`flex h-full flex-col rounded-xl border p-3 transition ${
                    isFocused
                      ? "border-[#9ef6ff] bg-[linear-gradient(160deg,#123866,#17457a,#2c2d6d)] shadow-[0_0_22px_rgba(109,219,255,0.45)]"
                      : "border-[#3a5f98] bg-[linear-gradient(160deg,#0d2347,#142f5b,#211d58)]"
                  }`}
                >
                  <img
                    src={product.image}
                    alt={normalizedProductId}
                    className="h-36 w-full rounded-lg border border-[#5d89c6] bg-[#0a1734] object-cover"
                  />
                  <div className="mt-3 text-sm font-black uppercase tracking-wide text-white">
                    {getLanguageCopy(language, product.name)}
                  </div>
                  <div className="mt-1 flex-1 text-xs leading-relaxed text-[#c0d6fb]">
                    {getLanguageCopy(language, product.description)}
                  </div>
                  <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8fd4ff]">
                    {accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
                      ? owned
                        ? getStoreDirectCopy(language, "enabled", "HABILITADO")
                        : normalizedProductId === TOP_REWARD_PRODUCT_ID
                          ? t("PROMOCION_ACTIVA_GANA_100_USD")
                          : `SE DESBLOQUEA SOLO SI ERES TOP #1 DEL MODO ${getTopWinnerModeLabel(
                              earnedMode
                            )}`
                      : owned
                        ? getStoreDirectCopy(language, "enabled", "HABILITADO")
                        : getAvailableWithBonusesLabel(language)}
                  </div>
                  {isScienceTopRewardProduct ? (
                    <div className="mt-2 rounded-lg border border-[#6f5db8] bg-[#1f2145]/80 p-2">
                      <div className="text-[10px] font-black uppercase tracking-[0.12em] text-[#bfaeff]">
                        {t("SCIENCE_TOP_MONTHLY_STATUS_TITLE")}
                      </div>
                      <div className={`mt-1 text-xs font-semibold ${scienceTopStatusToneClass}`}>
                        {scienceTopMonthlyRewardStatusLabel}
                      </div>
                      <div className="mt-1 text-[11px] text-[#dce6ff]">
                        {t("SCIENCE_TOP_MONTHLY_NEXT_RESET_LABEL")}:{" "}
                        {scienceTopMonthlyRewardNextResetLabel}
                      </div>
                      <div className="text-[11px] text-[#dce6ff]">
                        {t("SCIENCE_TOP_MONTHLY_TIME_LEFT_LABEL")}:{" "}
                        {scienceTopMonthlyRewardTimeRemainingLabel}
                      </div>
                    </div>
                  ) : null}
                  {isPriceEditMode && canEditPrice ? (
                    <button
                      type="button"
                      disabled={isUpdatingPrice}
                      onClick={() =>
                        handleEditProductPrice(
                          normalizedProductId,
                          productPriceX,
                          getLanguageCopy(language, product.name)
                        )
                      }
                      className="mt-2 rounded border border-dashed border-[#ffdd97] bg-[#4c390f] px-2 py-1 text-left text-lg font-black text-[#ffe7b5] transition hover:border-[#ffe8bf] disabled:opacity-70"
                    >
                      {isUpdatingPrice
                        ? "ACTUALIZANDO..."
                        : `${formatPriceXLabel(productPriceX)} (EDITAR)`}
                    </button>
                  ) : (
                    <div
                      className={`mt-2 text-lg font-black ${
                        product?.priceClassName || "text-[#73ff9c]"
                      }`}
                    >
                      {accessType === STORE_PRODUCT_ACCESS_PURCHASE
                        ? formatPriceXLabel(productPriceX)
                        : formatStorePrice(language, product.price)}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => handleOpenSpecialProduct(product)}
                    className="mt-3 rounded border border-[#4fd0ff] bg-[#11406a] px-3 py-2 text-center text-xs font-bold uppercase tracking-wide text-[#def6ff] transition hover:border-[#8be9ff] hover:brightness-110"
                  >
                    {t("VER_PRODUCTO")}
                  </button>
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => handleBuyProduct(product, BLOCK_TYPE_SPECIAL)}
                    className={`mt-2 rounded border px-3 py-2 text-center text-xs font-bold uppercase tracking-wide text-white transition ${
                      accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
                        ? "border-[#b18dff] bg-[#4d2b7d] hover:brightness-110"
                        : "border-[#8dc7ff] bg-[#244b78] hover:brightness-110"
                    } ${isBusy ? "opacity-70" : ""}`}
                  >
                    {isBusy
                      ? getStoreDirectCopy(language, "processing", "PROCESANDO...")
                      : owned
                        ? getStoreDirectCopy(language, "enabled", "HABILITADO")
                        : accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
                          ? normalizedProductId === TOP_REWARD_PRODUCT_ID
                            ? t("SOLICITAR_RECOMPENSA")
                            : `${getStoreDirectCopy(language, "get", "OBTENER")} TOP #1`
                          : formatBuyForPriceLabel(language, productPriceX)}
                  </button>
                </article>
              );
            })}
          </div>
        </section>

        <section className="rounded-2xl border border-[#2f4d7a] bg-[#081631d8] p-4 shadow-[0_0_26px_rgba(5,11,32,0.45)]">
          <div className="mb-4 flex flex-col gap-1">
            <h2 className="text-xl font-black uppercase tracking-wide text-[#9ed6ff]">
              {t("SKINS_DE_BLOQUES_COMUNES")}
            </h2>
            <p className="text-sm text-[#b6cbed]">
              {getStoreDirectCopy(
                language,
                "commonIntro",
                "Cada compra desbloquea el paquete para usarlo en la edicion de bloques comunes."
              )}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {COMMON_BLOCK_PRODUCTS.map((product) => {
              const normalizedProductId = normalizeStoreProductId(product?.id);
              const cardKey = `${BLOCK_TYPE_COMMON}:${normalizedProductId}`;
              const isBusy = purchaseBusyKey === cardKey;
              const isFocused = highlightProductKey === cardKey;
              const owned = isOwnedProduct(BLOCK_TYPE_COMMON, normalizedProductId);
              const productPriceX = resolveProductPriceX(
                normalizedProductId,
                DEFAULT_STORE_SKIN_PRICE_X
              );
              const canEditPrice = canEditPriceForProduct(
                normalizedProductId,
                STORE_PRODUCT_ACCESS_PURCHASE
              );
              const isUpdatingPrice = priceUpdateBusyKey === `price:${normalizedProductId}`;

              return (
                <article
                  key={normalizedProductId}
                  id={`store-${BLOCK_TYPE_COMMON}-${normalizedProductId}`}
                  className={`flex h-full flex-col rounded-xl border p-3 transition ${
                    isFocused
                      ? "border-[#9ef6ff] bg-[linear-gradient(160deg,#123866,#17457a,#2c2d6d)] shadow-[0_0_22px_rgba(109,219,255,0.45)]"
                      : "border-[#3a5f98] bg-[linear-gradient(160deg,#0d2347,#142f5b,#211d58)]"
                  }`}
                >
                  <img
                    src={product.image}
                    alt={normalizedProductId}
                    className="h-36 w-full rounded-lg border border-[#5d89c6] bg-[#0a1734] object-cover"
                  />
                  <div className="mt-3 text-sm font-black uppercase tracking-wide text-white">
                    {getLanguageCopy(language, product.name)}
                  </div>
                  <div className="mt-1 flex-1 text-xs leading-relaxed text-[#c0d6fb]">
                    {getLanguageCopy(language, product.description)}
                  </div>
                  <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8fd4ff]">
                    {owned
                      ? getStoreDirectCopy(language, "enabled", "HABILITADO")
                      : getAvailableWithBonusesLabel(language)}
                  </div>
                  {isPriceEditMode && canEditPrice ? (
                    <button
                      type="button"
                      disabled={isUpdatingPrice}
                      onClick={() =>
                        handleEditProductPrice(
                          normalizedProductId,
                          productPriceX,
                          getLanguageCopy(language, product.name)
                        )
                      }
                      className="mt-2 rounded border border-dashed border-[#ffdd97] bg-[#4c390f] px-2 py-1 text-left text-lg font-black text-[#ffe7b5] transition hover:border-[#ffe8bf] disabled:opacity-70"
                    >
                      {isUpdatingPrice
                        ? "ACTUALIZANDO..."
                        : `${formatPriceXLabel(productPriceX)} (EDITAR)`}
                    </button>
                  ) : (
                    <div className="mt-2 text-lg font-black text-[#73ff9c]">
                      {formatPriceXLabel(productPriceX)}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => handleOpenCommonProduct(product)}
                    className="mt-3 rounded border border-[#4fd0ff] bg-[#11406a] px-3 py-2 text-center text-xs font-bold uppercase tracking-wide text-[#def6ff] transition hover:border-[#8be9ff] hover:brightness-110"
                  >
                    {t("VER_PRODUCTO")}
                  </button>
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => handleBuyProduct(product, BLOCK_TYPE_COMMON)}
                    className={`mt-2 rounded border border-[#8dc7ff] bg-[#244b78] px-3 py-2 text-center text-xs font-bold uppercase tracking-wide text-white transition hover:brightness-110 ${
                      isBusy ? "opacity-70" : ""
                    }`}
                  >
                    {isBusy
                      ? getStoreDirectCopy(language, "processing", "PROCESANDO...")
                      : owned
                        ? getStoreDirectCopy(language, "enabled", "HABILITADO")
                        : formatBuyForPriceLabel(language, productPriceX)}
                  </button>
                </article>
              );
            })}
          </div>
        </section>

        <section className="rounded-2xl border border-[#3c78ad] bg-[#071525e8] p-4 shadow-[0_0_26px_rgba(56,189,248,0.12)]">
          <div className="mb-4 flex flex-col gap-1">
            <h2 className="text-xl font-black uppercase tracking-wide text-[#7de5ff]">
              ANIMACIONES DE CUBOS
            </h2>
            <p className="text-sm text-[#b8d9ed]">
              Desbloquea modelos tridimensionales animados para tus bloques comunes sin cambiar sus reglas, colisiones ni movimientos.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {BLOCK_ANIMATION_PRODUCTS.map((product) => {
              const normalizedProductId = normalizeStoreProductId(product?.id);
              const cardKey = `${BLOCK_TYPE_ANIMATION}:${normalizedProductId}`;
              const isBusy = purchaseBusyKey === cardKey;
              const isFocused = highlightProductKey === cardKey;
              const owned = isOwnedProduct(BLOCK_TYPE_ANIMATION, normalizedProductId);
              const productPriceX = resolveProductPriceX(normalizedProductId, 5000);
              const canEditPrice = canEditPriceForProduct(
                normalizedProductId,
                STORE_PRODUCT_ACCESS_PURCHASE
              );
              const isUpdatingPrice = priceUpdateBusyKey === `price:${normalizedProductId}`;
              return (
                <article
                  key={normalizedProductId}
                  id={`store-${BLOCK_TYPE_ANIMATION}-${normalizedProductId}`}
                  className={`flex h-full flex-col rounded-xl border p-3 transition ${
                    isFocused
                      ? "border-[#d7f6ff] bg-[#174763] shadow-[0_0_22px_rgba(56,189,248,0.45)]"
                      : "border-[#315e7d] bg-[linear-gradient(160deg,#071b2d,#0c2f4b,#08131f)]"
                  }`}
                >
                  <img
                    src={product.image}
                    alt={product.label}
                    className="h-36 w-full rounded-lg border border-[#3f789f] bg-[#030914] object-cover"
                  />
                  <div className="mt-3 text-sm font-black uppercase tracking-wide text-white">
                    {product.label}
                  </div>
                  <div className="mt-1 flex-1 text-xs leading-relaxed text-[#c6e3f3]">
                    {product.summary || "Modelo tridimensional ajustado automáticamente al tamaño del bloque de juego."}
                  </div>
                  <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#7de5ff]">
                    {owned ? "HABILITADA" : "DISPONIBLE PARA COMPRA CON BONOS"}
                  </div>
                  {isPriceEditMode && canEditPrice ? (
                    <button
                      type="button"
                      disabled={isUpdatingPrice}
                      onClick={() => handleEditProductPrice(normalizedProductId, productPriceX, product.label)}
                      className="mt-2 rounded border border-dashed border-[#99e8ff] bg-[#123b55] px-2 py-1 text-left text-lg font-black text-[#d8f7ff]"
                    >
                      {isUpdatingPrice ? "ACTUALIZANDO..." : `${formatPriceXLabel(productPriceX)} (EDITAR)`}
                    </button>
                  ) : (
                    <div className="mt-2 text-lg font-black text-[#73ff9c]">
                      {formatPriceXLabel(productPriceX)}
                    </div>
                  )}
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => handleBuyProduct(product, BLOCK_TYPE_ANIMATION)}
                    className={`mt-3 rounded border border-[#70d9ff] bg-[#15557a] px-3 py-2 text-center text-xs font-bold uppercase tracking-wide text-white transition hover:brightness-110 ${
                      isBusy ? "opacity-70" : ""
                    }`}
                  >
                    {isBusy
                      ? "PROCESANDO..."
                      : owned
                        ? "HABILITADA"
                        : formatBuyForPriceLabel(language, productPriceX)}
                  </button>
                </article>
              );
            })}
          </div>
        </section>

        <section className="rounded-2xl border border-[#6f5524] bg-[#151107e8] p-4 shadow-[0_0_26px_rgba(255,178,54,0.1)]">
          <div className="mb-4 flex flex-col gap-1">
            <h2 className="text-xl font-black uppercase tracking-wide text-[#ffd078]">
              TEXTURAS PBR PARA BLOQUES Y TABLEROS
            </h2>
            <p className="text-sm text-[#d8c9aa]">
              Cada compra desbloquea una textura para usarla tanto en tus bloques como en las paredes del tablero.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {MATERIAL_TEXTURE_PRODUCTS.map((product) => {
              const normalizedProductId = normalizeStoreProductId(product?.id);
              const cardKey = `${BLOCK_TYPE_MATERIAL}:${normalizedProductId}`;
              const isBusy = purchaseBusyKey === cardKey;
              const isFocused = highlightProductKey === cardKey;
              const owned = isOwnedProduct(BLOCK_TYPE_MATERIAL, normalizedProductId);
              const productPriceX = resolveProductPriceX(normalizedProductId, 5000);
              const canEditPrice = canEditPriceForProduct(
                normalizedProductId,
                STORE_PRODUCT_ACCESS_PURCHASE
              );
              const isUpdatingPrice = priceUpdateBusyKey === `price:${normalizedProductId}`;
              return (
                <article
                  key={normalizedProductId}
                  id={`store-${BLOCK_TYPE_MATERIAL}-${normalizedProductId}`}
                  className={`flex h-full flex-col rounded-xl border p-3 transition ${
                    isFocused
                      ? "border-[#fff0ad] bg-[#594317] shadow-[0_0_22px_rgba(255,209,102,0.42)]"
                      : "border-[#75592d] bg-[linear-gradient(160deg,#20180b,#31220d,#17130c)]"
                  }`}
                >
                  <img
                    src={product.image}
                    alt={product.label}
                    className="h-36 w-full rounded-lg border border-[#91703a] bg-[#0a0a08] object-cover"
                  />
                  <div className="mt-3 text-sm font-black uppercase tracking-wide text-white">
                    {product.label}
                  </div>
                  <div className="mt-1 flex-1 text-xs leading-relaxed text-[#dccfb8]">
                    Textura física PBR con color, relieve visual y respuesta realista a la luz.
                  </div>
                  <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#ffd078]">
                    {owned ? "HABILITADA" : "DISPONIBLE PARA COMPRA CON BONOS"}
                  </div>
                  {isPriceEditMode && canEditPrice ? (
                    <button
                      type="button"
                      disabled={isUpdatingPrice}
                      onClick={() => handleEditProductPrice(normalizedProductId, productPriceX, product.label)}
                      className="mt-2 rounded border border-dashed border-[#ffdd97] bg-[#4c390f] px-2 py-1 text-left text-lg font-black text-[#ffe7b5]"
                    >
                      {isUpdatingPrice ? "ACTUALIZANDO..." : `${formatPriceXLabel(productPriceX)} (EDITAR)`}
                    </button>
                  ) : (
                    <div className="mt-2 text-lg font-black text-[#73ff9c]">
                      {formatPriceXLabel(productPriceX)}
                    </div>
                  )}
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => handleBuyProduct(product, BLOCK_TYPE_MATERIAL)}
                    className={`mt-3 rounded border border-[#dba84f] bg-[#624414] px-3 py-2 text-center text-xs font-bold uppercase tracking-wide text-white transition hover:brightness-110 ${
                      isBusy ? "opacity-70" : ""
                    }`}
                  >
                    {isBusy
                      ? "PROCESANDO..."
                      : owned
                        ? "HABILITADA"
                        : formatBuyForPriceLabel(language, productPriceX)}
                  </button>
                </article>
              );
            })}
          </div>
        </section>

        {GIFT_CARD_PRODUCTS.length > 0 ? (
          <section className="mt-6">
            <h2 className="text-xl font-black uppercase tracking-wide text-[#7de5ff]">
              {t("TARJETAS_DE_REGALO")}
            </h2>

            {giftCardAvailabilityMeta?.userClaimedToday ? (
              <p className="mt-2 rounded-lg border border-[#8f6a2a] bg-[#3c2a0e] px-3 py-2 text-xs text-[#ffe4aa]">
                {t("giftCardDailyUserLimitMessage")}
              </p>
            ) : null}

            {giftCardAvailabilityMeta?.allSoldOut || areAllGiftCardsSoldOut ? (
              <div className="mt-4 rounded-xl border border-[#7d4b2f] bg-[#3d1f14] p-4 text-sm leading-relaxed text-[#ffd8c4]">
                {t("giftCardSoldOutMessage")}
              </div>
            ) : (
              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {giftCardAvailabilityList.map((entry) => {
                  const product = entry.product;
                  const normalizedProductId = entry.productId;
                  const canClaim =
                    entry.isAvailableForClaim && !giftCardAvailabilityMeta?.userClaimedToday;
                  const isBusy =
                    boardCheckoutBusy &&
                    normalizeStoreProductId(activeRewardProduct?.id) === normalizedProductId;

                  return (
                    <article
                      key={normalizedProductId}
                      className="rounded-2xl border border-[#315f8f] bg-[#091f3c] p-3"
                    >
                      <img
                        src={product?.image}
                        alt={getLanguageCopy(language, product?.name)}
                        className="h-44 w-full rounded-xl border border-[#4f7db8] bg-[#0a1734] object-contain"
                      />
                      <h3 className="mt-3 text-base font-black uppercase tracking-wide text-[#b9dcff]">
                        {getLanguageCopy(language, product?.name)}
                      </h3>
                      <p className="mt-2 min-h-12 text-xs text-[#acc7ea]">
                        {getLanguageCopy(language, product?.description)}
                      </p>
                      <div className="mt-3 text-lg font-black text-[#73ff9c]">
                        {formatPriceXLabel(entry.priceX)}
                      </div>
                      <div className="mt-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9ed6ff]">
                        {`${t("giftCardAvailabilityLabel")}: ${entry.availableToday}/${entry.maxDailyQuantity}`}
                      </div>
                      {!entry.isActive ? (
                        <div className="mt-1 text-[11px] text-[#ffb38f]">
                          {t("giftCardInactiveLabel")}
                        </div>
                      ) : null}
                      {!entry.isAvailableForClaim ? (
                        <div className="mt-1 text-[11px] text-[#ffcf9e]">
                          {t("giftCardNoAvailability")}
                        </div>
                      ) : null}
                      <button
                        type="button"
                        disabled={isBusy || !canClaim}
                        onClick={() => handleBuyProduct(product, BLOCK_TYPE_SPECIAL)}
                        className={`mt-3 w-full rounded border border-[#8dc7ff] bg-[#244b78] px-3 py-2 text-center text-xs font-bold uppercase tracking-wide text-white transition hover:brightness-110 ${
                          isBusy || !canClaim ? "opacity-70" : ""
                        }`}
                      >
                        {isBusy
                          ? getStoreDirectCopy(language, "processing", "PROCESANDO...")
                          : giftCardAvailabilityMeta?.userClaimedToday
                            ? t("giftCardClaimedToday")
                            : !entry.isAvailableForClaim
                              ? t("giftCardNoAvailability")
                              : t("SOLICITAR_RECOMPENSA")}
                      </button>
                    </article>
                  );
                })}
              </div>
            )}

            {isPriceEditMode && hasPriceEditAccess ? (
              <div className="mt-4 rounded-2xl border border-[#3a5f98] bg-[#0b2345] p-4">
                <div className="mb-2 text-sm font-black uppercase tracking-[0.1em] text-[#a7ddff]">
                  {t("giftCardDailyConfigTitle")}
                </div>
                <p className="mb-4 text-xs text-[#c3dcff]">
                  {t("giftCardDailyConfigHint")}
                </p>
                <div className="grid gap-3 lg:grid-cols-3">
                  {giftCardAvailabilityList.map((entry) => {
                    const normalizedProductId = entry.productId;
                    const draft = giftCardConfigDraftById?.[normalizedProductId] || {
                      priceX: String(entry.priceX),
                      availableToday: String(entry.availableToday),
                      maxDailyQuantity: String(entry.maxDailyQuantity),
                      isActive: entry.isActive,
                    };
                    const isSaving = giftCardConfigBusyKey === `gift-config:${normalizedProductId}`;
                    return (
                      <article
                        key={`admin-${normalizedProductId}`}
                        className="rounded-xl border border-[#4a6b9f] bg-[#0f2e57] p-3"
                      >
                        <h3 className="text-sm font-black uppercase tracking-wide text-[#d0e9ff]">
                          {getLanguageCopy(language, entry.product?.name)}
                        </h3>
                        <label className="mt-2 block text-[11px] font-semibold uppercase tracking-wide text-[#9bc8f0]">
                          {t("giftCardAdminPriceXLabel")}
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={draft.priceX}
                          onChange={(event) =>
                            handleGiftCardConfigFieldChange(
                              normalizedProductId,
                              "priceX",
                              event.target.value
                            )
                          }
                          className="mt-1 w-full rounded border border-[#4f7bb1] bg-[#0d274c] px-3 py-2 text-sm text-[#e9f3ff] outline-none placeholder:text-[#9dbce4] focus:border-[#7bc0ff]"
                        />

                        <label className="mt-2 block text-[11px] font-semibold uppercase tracking-wide text-[#9bc8f0]">
                          {t("giftCardAdminAvailableTodayLabel")}
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={draft.availableToday}
                          onChange={(event) =>
                            handleGiftCardConfigFieldChange(
                              normalizedProductId,
                              "availableToday",
                              event.target.value
                            )
                          }
                          className="mt-1 w-full rounded border border-[#4f7bb1] bg-[#0d274c] px-3 py-2 text-sm text-[#e9f3ff] outline-none placeholder:text-[#9dbce4] focus:border-[#7bc0ff]"
                        />

                        <label className="mt-2 block text-[11px] font-semibold uppercase tracking-wide text-[#9bc8f0]">
                          {t("giftCardAdminMaxDailyLabel")}
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={draft.maxDailyQuantity}
                          onChange={(event) =>
                            handleGiftCardConfigFieldChange(
                              normalizedProductId,
                              "maxDailyQuantity",
                              event.target.value
                            )
                          }
                          className="mt-1 w-full rounded border border-[#4f7bb1] bg-[#0d274c] px-3 py-2 text-sm text-[#e9f3ff] outline-none placeholder:text-[#9dbce4] focus:border-[#7bc0ff]"
                        />

                        <label className="mt-3 flex items-center gap-2 text-xs text-[#d8ebff]">
                          <input
                            type="checkbox"
                            checked={draft.isActive === true}
                            onChange={(event) =>
                              handleGiftCardConfigFieldChange(
                                normalizedProductId,
                                "isActive",
                                event.target.checked
                              )
                            }
                          />
                          <span>{t("giftCardAdminActiveLabel")}</span>
                        </label>

                        <button
                          type="button"
                          disabled={isSaving}
                          onClick={() => handleSaveGiftCardDailyConfig(normalizedProductId)}
                          className={`mt-3 w-full rounded border border-[#8dc7ff] bg-[#244b78] px-3 py-2 text-center text-xs font-bold uppercase tracking-wide text-white transition hover:brightness-110 ${
                            isSaving ? "opacity-70" : ""
                          }`}
                        >
                          {isSaving ? t("giftCardAdminSaving") : t("giftCardAdminSaveButton")}
                        </button>
                      </article>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        <section className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
          <article className="rounded-2xl border border-[#3a5f98] bg-[#091938db] p-4">
            <h2 className="text-xl font-black uppercase tracking-wide text-[#9ed6ff]">
              {t("JUEGO_DE_MESA_XIMA")}
            </h2>
            <p className="mt-2 text-sm text-[#b9cff1]">
              {getStoreDirectCopy(
                language,
                "boardGameDesc",
                "Version fisica oficial para jugar en casa, practicar estrategia y compartir con amigos."
              )}
            </p>
            <img
              src={BOARD_GAME_GALLERY[0]}
              alt={i18nT("store.ximaBoardGame")}
              className="mt-3 h-56 w-full rounded-xl border border-[#5d89c6] bg-[#091327] object-cover md:h-72"
            />
            {isPriceEditMode && canEditPriceForProduct(BOARD_GAME_PRODUCT_ID) ? (
              <button
                type="button"
                disabled={priceUpdateBusyKey === `price:${BOARD_GAME_PRODUCT_ID}`}
                onClick={() => handleEditProductPrice(BOARD_GAME_PRODUCT_ID, boardGamePriceX, "Juego de Mesa XIMA")}
                className="mt-3 rounded border border-dashed border-[#ffdd97] bg-[#4c390f] px-3 py-2 text-left text-lg font-black text-[#ffe7b5] transition hover:border-[#ffe8bf] disabled:opacity-70"
              >
                {priceUpdateBusyKey === `price:${BOARD_GAME_PRODUCT_ID}`
                  ? "ACTUALIZANDO..."
                  : `${formatPriceXLabel(boardGamePriceX)} (EDITAR)`}
              </button>
            ) : (
              <div className="mt-3 text-3xl font-black text-[#73ff9c]">
                {formatPriceXLabel(boardGamePriceX)}
              </div>
            )}
            <button
              type="button"
              onClick={handleOpenBoardProduct}
              className="mt-4 inline-flex rounded border border-[#66ffa2] bg-[#1b7f49] px-4 py-2 text-sm font-black uppercase tracking-wide text-white transition hover:brightness-110"
            >
              {t("VER_PRODUCTO")}
            </button>
            <button
              type="button"
              onClick={handleOpenBoardCheckout}
              className="ml-2 mt-4 inline-flex rounded border border-[#66ffa2] bg-[#1b7f49] px-4 py-2 text-sm font-black uppercase tracking-wide text-white transition hover:brightness-110"
            >
              {`${formatGetForPriceLabel(language, boardGamePriceX)} X`}
            </button>
          </article>

          <article className="rounded-2xl border border-[#5d4a8a] bg-[linear-gradient(155deg,#1a1740,#1b2252,#2c1b4f)] p-4">
            <h2 className="text-xl font-black uppercase tracking-wide text-[#d7b5ff]">
              {t("SUSCRIPCION_XIMA_PLUS")}
            </h2>
            <p className="mt-2 text-sm text-[#dbcdf4]">
              {getStoreDirectCopy(
                language,
                "plusDesc",
                "Acceso a recompensas, cosmeticos exclusivos, ventajas de comunidad y experiencia sin anuncios."
              )}
            </p>
            <div className="mt-4 rounded-xl border border-[#8a6ec2] bg-[#2a2350] p-4">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#ffcf7f]">
                {t("ESTADO")}
              </div>
              <div className="mt-2 text-lg font-black text-[#ffe7b9]">
                {t("NO_ACTIVA_POR_EL_MOMENTO")}
              </div>
              <div className="mt-1 text-sm text-[#d9caf5]">
                {t("PROXIMAMENTE_HABILITAREMOS_LA_SUSCRIPCION")}
              </div>
            </div>
            <button
              type="button"
              disabled
              className="mt-4 w-full cursor-not-allowed rounded border border-[#8774ae] bg-[#3a3262] px-4 py-2 text-sm font-bold uppercase tracking-wide text-[#d7c6f3] opacity-80"
            >
              {getStoreDirectCopy(language, "notAvailable", "No disponible")}
            </button>
          </article>
        </section>
      </div>

      {selectedSpecialProduct ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#030712cc] p-4">
          <div className="w-full max-w-3xl rounded-2xl border border-[#4a6fa8] bg-[#081b3a] p-4 shadow-[0_0_32px_rgba(0,135,255,0.28)]">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-black uppercase tracking-wide text-[#b7dcff]">
                  {getLanguageCopy(language, selectedSpecialProduct.name)}
                </h3>
                <p className="text-sm text-[#c7daf8]">
                  {getLanguageCopy(language, selectedSpecialProduct.description)}
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseSpecialProduct}
                className="rounded border border-[#6f8ebc] bg-[#243f69] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#e7f2ff] transition hover:border-[#9ac2ff]"
              >
                {t("CERRAR")}
              </button>
            </div>

            <img
              src={selectedSpecialImage || selectedSpecialProduct.image}
              alt={selectedSpecialProduct.id}
              className="h-64 w-full rounded-xl border border-[#5d89c6] bg-[#0a1734] object-contain md:h-80"
            />

            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {selectedSpecialGallery.map((imagePath) => {
                const isSelected = imagePath === selectedSpecialImage;
                return (
                  <button
                    key={imagePath}
                    type="button"
                    onClick={() => setSelectedSpecialImage(imagePath)}
                    className={`rounded border p-1 transition ${
                      isSelected
                        ? "border-[#8bd6ff] bg-[#1c4776]"
                        : "border-[#4a6a95] bg-[#112a4a] hover:border-[#6faee6]"
                    }`}
                  >
                    <img
                      src={imagePath}
                      alt={selectedSpecialProduct.id}
                      className="h-20 w-full rounded bg-[#081327] object-cover"
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      {selectedCommonProduct ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#030712cc] p-4">
          <div className="w-full max-w-3xl rounded-2xl border border-[#4a6fa8] bg-[#081b3a] p-4 shadow-[0_0_32px_rgba(0,135,255,0.28)]">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-black uppercase tracking-wide text-[#b7dcff]">
                  {getLanguageCopy(language, selectedCommonProduct.name)}
                </h3>
                <p className="text-sm text-[#c7daf8]">
                  {getLanguageCopy(language, selectedCommonProduct.description)}
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseCommonProduct}
                className="rounded border border-[#6f8ebc] bg-[#243f69] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#e7f2ff] transition hover:border-[#9ac2ff]"
              >
                {t("CERRAR")}
              </button>
            </div>

            <img
              src={selectedCommonImage || selectedCommonProduct.image}
              alt={selectedCommonProduct.id}
              className="h-64 w-full rounded-xl border border-[#5d89c6] bg-[#0a1734] object-contain md:h-80"
            />

            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {selectedCommonGallery.map((imagePath) => {
                const isSelected = imagePath === selectedCommonImage;
                return (
                  <button
                    key={imagePath}
                    type="button"
                    onClick={() => setSelectedCommonImage(imagePath)}
                    className={`rounded border p-1 transition ${
                      isSelected
                        ? "border-[#8bd6ff] bg-[#1c4776]"
                        : "border-[#4a6a95] bg-[#112a4a] hover:border-[#6faee6]"
                    }`}
                  >
                    <img
                      src={imagePath}
                      alt={selectedCommonProduct.id}
                      className="h-20 w-full rounded bg-[#081327] object-cover"
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      {isBoardProductModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#030712cc] p-4">
          <div className="w-full max-w-3xl rounded-2xl border border-[#4a6fa8] bg-[#081b3a] p-4 shadow-[0_0_32px_rgba(0,135,255,0.28)]">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-black uppercase tracking-wide text-[#b7dcff]">
                  {t("JUEGO_DE_MESA_XIMA")}
                </h3>
                <p className="text-sm text-[#c7daf8]">
                  {getStoreDirectCopy(
                    language,
                    "boardGameDesc",
                    "Version fisica oficial para jugar en casa, practicar estrategia y compartir con amigos."
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseBoardProduct}
                className="rounded border border-[#6f8ebc] bg-[#243f69] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#e7f2ff] transition hover:border-[#9ac2ff]"
              >
                {t("CERRAR")}
              </button>
            </div>

            <img
              src={selectedBoardImage}
              alt={i18nT("store.juegoDeMesaXima")}
              className="h-64 w-full rounded-xl border border-[#5d89c6] bg-[#0a1734] object-contain md:h-80"
            />

            <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6">
              {BOARD_GAME_GALLERY.map((imagePath) => {
                const isSelected = imagePath === selectedBoardImage;
                return (
                  <button
                    key={imagePath}
                    type="button"
                    onClick={() => setSelectedBoardImage(imagePath)}
                    className={`rounded border p-1 transition ${
                      isSelected
                        ? "border-[#8bd6ff] bg-[#1c4776]"
                        : "border-[#4a6a95] bg-[#112a4a] hover:border-[#6faee6]"
                    }`}
                  >
                    <img
                      src={imagePath}
                      alt={i18nT("store.juegoDeMesaXima")}
                      className="h-16 w-full rounded bg-[#081327] object-cover"
                    />
                  </button>
                );
              })}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <div className="text-2xl font-black text-[#73ff9c]">{formatPriceXLabel(boardGamePriceX)}</div>
              <button
                type="button"
                onClick={handleOpenBoardCheckout}
                className="rounded border border-[#66ffa2] bg-[#1b7f49] px-4 py-2 text-sm font-black uppercase tracking-wide text-white transition hover:brightness-110"
              >
                {`${formatGetForPriceLabel(language, boardGamePriceX)} X`}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isBoardCheckoutModalOpen ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#030712cc] p-4">
          <div className="w-full max-w-3xl rounded-2xl border border-[#4a6fa8] bg-[#081b3a] p-4 shadow-[0_0_32px_rgba(0,135,255,0.28)]">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-black uppercase tracking-wide text-[#b7dcff]">
                  {String(activeRewardProduct?.name || "").trim() || t("JUEGO_DE_MESA_XIMA")}
                </h3>
                <p className="text-sm text-[#c7daf8]">{t("rewardClaimModalDescription")}</p>
              </div>
              <button
                type="button"
                onClick={handleCloseBoardCheckout}
                className="rounded border border-[#6f8ebc] bg-[#243f69] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#e7f2ff] transition hover:border-[#9ac2ff]"
              >
                {t("CERRAR")}
              </button>
            </div>

            <div className="mb-3 text-3xl font-black text-[#73ff9c]">
              {formatPriceXLabel(activeRewardProduct?.priceX || boardGamePriceX)}
            </div>
            <p className="mb-3 rounded-lg border border-[#5a83b6] bg-[#102446] p-3 text-xs leading-relaxed text-[#dcecff]">
              {t("rewardClaimInfoWarning")}
            </p>

            <div className="grid gap-2 md:grid-cols-2">
              <input
                type="text"
                value={rewardClaimForm.firstName}
                onChange={(event) => handleRewardClaimFieldChange("firstName", event.target.value)}
                placeholder={t("firstName")}
                className="rounded border border-[#4f7bb1] bg-[#0d274c] px-3 py-2 text-sm text-[#e9f3ff] outline-none placeholder:text-[#9dbce4] focus:border-[#7bc0ff]"
              />
              <input
                type="text"
                value={rewardClaimForm.lastName}
                onChange={(event) => handleRewardClaimFieldChange("lastName", event.target.value)}
                placeholder={t("lastName")}
                className="rounded border border-[#4f7bb1] bg-[#0d274c] px-3 py-2 text-sm text-[#e9f3ff] outline-none placeholder:text-[#9dbce4] focus:border-[#7bc0ff]"
              />
              <input
                type="email"
                value={rewardClaimForm.contactEmail}
                onChange={(event) =>
                  handleRewardClaimFieldChange("contactEmail", event.target.value)
                }
                placeholder={t("contactEmail")}
                className="rounded border border-[#4f7bb1] bg-[#0d274c] px-3 py-2 text-sm text-[#e9f3ff] outline-none placeholder:text-[#9dbce4] focus:border-[#7bc0ff]"
              />
              <input
                type="text"
                value={rewardClaimForm.phone}
                onChange={(event) => handleRewardClaimFieldChange("phone", event.target.value)}
                placeholder={t("phone")}
                className="rounded border border-[#4f7bb1] bg-[#0d274c] px-3 py-2 text-sm text-[#e9f3ff] outline-none placeholder:text-[#9dbce4] focus:border-[#7bc0ff]"
              />
              <input
                type="text"
                value={rewardClaimForm.country}
                onChange={(event) => handleRewardClaimFieldChange("country", event.target.value)}
                placeholder={t("country")}
                className="rounded border border-[#4f7bb1] bg-[#0d274c] px-3 py-2 text-sm text-[#e9f3ff] outline-none placeholder:text-[#9dbce4] focus:border-[#7bc0ff]"
              />
              <input
                type="text"
                value={rewardClaimForm.stateProvince}
                onChange={(event) =>
                  handleRewardClaimFieldChange("stateProvince", event.target.value)
                }
                placeholder={t("stateProvince")}
                className="rounded border border-[#4f7bb1] bg-[#0d274c] px-3 py-2 text-sm text-[#e9f3ff] outline-none placeholder:text-[#9dbce4] focus:border-[#7bc0ff]"
              />
              <input
                type="text"
                value={rewardClaimForm.city}
                onChange={(event) => handleRewardClaimFieldChange("city", event.target.value)}
                placeholder={t("city")}
                className="rounded border border-[#4f7bb1] bg-[#0d274c] px-3 py-2 text-sm text-[#e9f3ff] outline-none placeholder:text-[#9dbce4] focus:border-[#7bc0ff]"
              />
              <input
                type="text"
                value={rewardClaimForm.addressLine1}
                onChange={(event) =>
                  handleRewardClaimFieldChange("addressLine1", event.target.value)
                }
                placeholder={t("shippingAddressLine1")}
                className="rounded border border-[#4f7bb1] bg-[#0d274c] px-3 py-2 text-sm text-[#e9f3ff] outline-none placeholder:text-[#9dbce4] focus:border-[#7bc0ff] md:col-span-2"
              />
              <input
                type="text"
                value={rewardClaimForm.addressLine2}
                onChange={(event) =>
                  handleRewardClaimFieldChange("addressLine2", event.target.value)
                }
                placeholder={t("addressLine2")}
                className="rounded border border-[#4f7bb1] bg-[#0d274c] px-3 py-2 text-sm text-[#e9f3ff] outline-none placeholder:text-[#9dbce4] focus:border-[#7bc0ff] md:col-span-2"
              />
              <input
                type="text"
                value={rewardClaimForm.postalCode}
                onChange={(event) =>
                  handleRewardClaimFieldChange("postalCode", event.target.value)
                }
                placeholder={t("postalCode")}
                className="rounded border border-[#4f7bb1] bg-[#0d274c] px-3 py-2 text-sm text-[#e9f3ff] outline-none placeholder:text-[#9dbce4] focus:border-[#7bc0ff]"
              />
            </div>

            <label className="mt-3 flex items-start gap-2 rounded border border-[#4f7bb1] bg-[#0d274c] px-3 py-2 text-xs text-[#d8ebff]">
              <input
                type="checkbox"
                checked={rewardClaimForm.confirmationAccepted}
                onChange={(event) =>
                  handleRewardClaimFieldChange("confirmationAccepted", event.target.checked)
                }
                className="mt-0.5"
              />
              <span>{t("confirmRealDataLabel")}</span>
            </label>

            <div className="mt-3 text-xs text-[#ffcf9f]">
              {t("rewardClaimDataWarning")}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={boardCheckoutBusy}
                onClick={handleConfirmBoardPurchase}
                className={`rounded border border-[#66ffa2] bg-[#1b7f49] px-4 py-2 text-sm font-black uppercase tracking-wide text-white transition hover:brightness-110 ${
                  boardCheckoutBusy ? "opacity-70" : ""
                }`}
              >
                {boardCheckoutBusy
                  ? getStoreDirectCopy(language, "processing", "PROCESANDO...")
                  : t("submitRewardClaim")}
              </button>
              <button
                type="button"
                disabled={boardCheckoutBusy}
                onClick={handleCloseBoardCheckout}
                className="rounded border border-[#7da8db] bg-[#27436d] px-4 py-2 text-sm font-black uppercase tracking-wide text-white transition hover:brightness-110 disabled:opacity-60"
              >
                {t("cancel")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isConfirmModalOpen ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-[#030712cc] p-4">
          <div className="w-full max-w-lg rounded-2xl border border-[#4a6fa8] bg-[#081b3a] p-4 shadow-[0_0_32px_rgba(0,135,255,0.28)]">
            <p className="whitespace-pre-line text-sm leading-relaxed text-[#e6f1ff]">
              {confirmMessage}
            </p>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => resolveStoreConfirmation(true)}
                className="rounded border border-[#66ffa2] bg-[#1b7f49] px-4 py-2 text-sm font-black uppercase tracking-wide text-white transition hover:brightness-110"
              >
                {t("accept")}
              </button>
              {confirmRequiresChoice ? (
                <button
                  type="button"
                  onClick={() => resolveStoreConfirmation(false)}
                  className="rounded border border-[#7da8db] bg-[#27436d] px-4 py-2 text-sm font-black uppercase tracking-wide text-white transition hover:brightness-110"
                >
                  {t("cancel")}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Store;
